import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaTimes,
  FaFlag,
  FaRegFlag,
  FaBookmark,
  FaRegBookmark,
  FaPlusCircle,
} from "react-icons/fa";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import DoubtSolver from "./DoubtSolver";
import SimpleThreeDotsLoader from "./SimpleThreeDotsLoader";
import MathJaxRenderer from "./MathJaxRenderer";
import ReactCrop, { convertToPixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  isMasteryFlowActive,
  deactivateMasteryFlow,
} from "../utils/masteryFlow";
import {
  clearQuizStateFromWindow,
  getStoredQuizState,
} from "../utils/quizState";

const mathJaxConfig = {
  tex: { inlineMath: [["$", "$"]] },
};

const FLAG_REASON_OPTIONS = [
  "Question is wrong",
  "Answer options are wrong",
  "Right option is marked as wrong",
  "Poorly formatted question",
  "Poorly formatted options",
  "Image is not displayed properly",
  "Other, please specify",
];

// Stored in lowercase because question_type is normalized to lowercase
const DESCRIPTIVE_TYPES = ["vsa", "sa", "la", "long answer", "short answer"];

const DESCRIPTIVE_CONFIG = {
  vsa: {
    label: "Very Short Answer",
    maxWords: 1000,
    minHeightPx: 140,
    maxViewportHeightRatio: 0.35,
  },
  sa: {
    label: "Short Answer",
    maxWords: 1000,
    minHeightPx: 180,
    maxViewportHeightRatio: 0.45,
  },
  la: {
    label: "Long Answer",
    maxWords: 1000,
    minHeightPx: 220,
    maxViewportHeightRatio: 0.6,
  },
};

const DEFAULT_DESCRIPTIVE_CONFIG = {
  label: "Descriptive Answer",
  maxWords: 1000,
  minHeightPx: 160,
  maxViewportHeightRatio: 0.5,
};

// Messages shown while descriptive answers are being processed
const DESCRIPTIVE_STATUS_MESSAGES = [
  "Thinking about your answer...",
  "Reading your response carefully...",
  "Understanding your answer and structure...",
  "Evaluating your response in detail...",
];

const LEVEL_UP_CELEBRATION_DURATION = 4000;

const applyBasicMarkdown = (text) => {
  if (!text) return text;
  try {
    return text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  } catch {
    return text;
  }
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getRadianAngle = (degreeValue) => (degreeValue * Math.PI) / 180;

const rotateSize = (width, height, rotation) => {
  const rotRad = getRadianAngle(rotation);
  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

const getCroppedImageBlob = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Unable to create canvas context for cropping");
  }

  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  const roundedCrop = {
    x: Math.round(pixelCrop?.x ?? 0),
    y: Math.round(pixelCrop?.y ?? 0),
    width: Math.round(pixelCrop?.width ?? image.width),
    height: Math.round(pixelCrop?.height ?? image.height),
  };

  const imageData = ctx.getImageData(
    roundedCrop.x,
    roundedCrop.y,
    roundedCrop.width,
    roundedCrop.height
  );

  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = roundedCrop.width;
  outputCanvas.height = roundedCrop.height;
  const outputCtx = outputCanvas.getContext("2d");
  if (!outputCtx) {
    throw new Error("Unable to create output canvas context");
  }
  outputCtx.putImageData(imageData, 0, 0);

  return new Promise((resolve, reject) => {
    outputCanvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to create cropped image blob"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.95
    );
  });
};

const FeedbackContent = ({ row, onAskDoubt }) => {
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [preview, setPreview] = useState("");
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  const feedback = row?.solution || "-";
  const marks = row?.marks ?? 0;
  const maxMarks = row?.maxMarks ?? 0;

  const getLineHeight = (el) => {
    const cs = window.getComputedStyle(el);
    let lh = parseFloat(cs.lineHeight);
    if (!lh || Number.isNaN(lh)) {
      const fs = parseFloat(cs.fontSize) || 16;
      lh = fs * 1.2;
    }
    return lh;
  };

  const countVisualLines = (el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    const rects = Array.from(range.getClientRects()).filter(
      (r) => r.width > 0 && r.height > 0
    );
    if (!rects.length) return 0;

    const lh = getLineHeight(el);
    const THRESH = Math.max(2, lh * 0.4);
    const MIN_HEIGHT = Math.max(1, lh * 0.35);

    rects.sort((a, b) => a.top - b.top);

    let lines = 0,
      currentTop = null;
    for (const r of rects) {
      if (r.height < MIN_HEIGHT) continue;
      if (currentTop === null || Math.abs(r.top - currentTop) > THRESH) {
        lines += 1;
        currentTop = r.top;
      }
    }
    return lines;
  };

  const estimateCharsPerLine = (el) => {
    const cs = window.getComputedStyle(el);
    const font = `${cs.fontStyle} ${cs.fontVariant} ${cs.fontWeight} ${cs.fontSize} / ${cs.lineHeight} ${cs.fontFamily}`;
    const width = el.clientWidth || 0;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = font;

    const sample =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ 0123456789";
    const w = ctx.measureText(sample).width || 1;
    const avgChar = w / sample.length;
    if (avgChar <= 0) return 50;
    return Math.max(10, Math.floor(width / avgChar));
  };

  const makePreview = (sourceEl, text) => {
    if (!sourceEl) return "";
    const clean = String(text).replace(/\s+/g, " ").trim();
    const cpl = estimateCharsPerLine(sourceEl);
    const maxChars = Math.max(20, Math.floor(cpl * 2 * 0.95));
    if (clean.length <= maxChars) return clean;
    let cut = clean.slice(0, maxChars);
    const lastSpace = cut.lastIndexOf(" ");
    if (lastSpace > 0) cut = cut.slice(0, lastSpace);
    return cut.trim() + ".";
  };

  useEffect(() => {
    const measure = () => {
      const content = contentRef.current;
      const container = containerRef.current;
      if (!content || !container) return;
      const prevDisplay = content.style.display;
      content.style.display = "block";
      requestAnimationFrame(() => {
        const lines = countVisualLines(content);
        setShowToggle(lines > 2);
        setPreview(makePreview(container, content.textContent || ""));
        content.style.display = prevDisplay || "";
      });
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);

    const mo = new MutationObserver(() => measure());
    if (contentRef.current)
      mo.observe(contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });

    const onWinResize = () => measure();
    window.addEventListener("resize", onWinResize);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", onWinResize);
    };
  }, [feedback]);

  return (
    <>
      {feedback && feedback !== "-" ? (
        <>
          <div ref={containerRef} style={{ width: "100%", minWidth: 0 }}>
            <div ref={contentRef} className="text-gray-700">
              <MathJaxRenderer>{applyBasicMarkdown(feedback)}</MathJaxRenderer>
            </div>
          </div>
        </>
      ) : (
        <span>-</span>
      )}

      {marks < maxMarks && typeof onAskDoubt === "function" && (
        <div>
          <button
            className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 rounded-sm"
            onClick={onAskDoubt}
          >
            Why did I lose marks?
          </button>
        </div>
      )}
    </>
  );
};

const normalizeStepBreakdown = (rawSteps = []) => {
  if (!Array.isArray(rawSteps)) {
    return [];
  }

  return rawSteps.map((step = {}, index) => ({
    ...step,
    step: step?.step ?? index + 1,
    feedback: step?.feedback ?? "",
    marks: typeof step?.marks === "number" ? step.marks : 0,
    maxMarks: typeof step?.maxMarks === "number" ? step.maxMarks : 0,
  }));
};

const normalizeDescriptiveEvaluation = (evaluation = null) => {
  if (!evaluation || typeof evaluation !== "object") {
    return null;
  }

  const stepBreakdown = normalizeStepBreakdown(evaluation?.stepBreakdown);
  const accumulatedMax = stepBreakdown.reduce((total, step) => {
    return total + (typeof step.maxMarks === "number" ? step.maxMarks : 0);
  }, 0);
  const accumulatedMarks = stepBreakdown.reduce((total, step) => {
    return total + (typeof step.marks === "number" ? step.marks : 0);
  }, 0);
  const score =
    typeof evaluation?.score === "number"
      ? evaluation.score
      : typeof evaluation?.marks === "number"
      ? evaluation.marks
      : accumulatedMarks;
  const totalMarks =
    typeof evaluation?.totalMarks === "number"
      ? evaluation.totalMarks
      : typeof evaluation?.maxMarks === "number"
      ? evaluation.maxMarks
      : accumulatedMax;
  const feedback =
    typeof evaluation?.feedback === "string"
      ? evaluation.feedback
      : evaluation?.feedback != null
      ? String(evaluation.feedback)
      : "";

  return {
    ...evaluation,
    score,
    totalMarks,
    feedback,
    stepBreakdown,
  };
};

const formatMarks = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(1);
};

const getFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
const ATTACHMENT_PREVIEW_MIN_ZOOM = 0.5;
const ATTACHMENT_PREVIEW_MAX_ZOOM = 3;
const ATTACHMENT_PREVIEW_ZOOM_STEP = 0.1;

const countWords = (text = "") => {
  if (typeof text !== "string") return 0;
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
};

const GoalQuestions = ({ embedded = false, onClose }) => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const initialQuizState = useMemo(() => getStoredQuizState(), []);
  const storedTopicId = initialQuizState.topicId;
  const questions = initialQuizState.questions ?? [];
  const goalDetails = initialQuizState.goalDetails ?? {};
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
    typeof initialQuizState.currentQuestionIndex === "number"
      ? initialQuizState.currentQuestionIndex
      : 0
  );
  const [selectedOption, setSelectedOption] = useState(
    typeof initialQuizState.selectedOption === "number"
      ? initialQuizState.selectedOption
      : null
  );
  const [score, setScore] = useState(
    Number.isFinite(initialQuizState.score) ? initialQuizState.score : 0
  );
  const [showExplanation, setShowExplanation] = useState(
    Boolean(initialQuizState.showExplanation)
  );
  const [quizCompleted, setQuizCompleted] = useState(
    Boolean(initialQuizState.quizCompleted)
  );
  const [leveledUp, setLeveledUp] = useState(
    Boolean(initialQuizState.leveledUp)
  );
  const [showLevelUpCelebration, setShowLevelUpCelebration] = useState(false);
  const [goalStatus, setGoalStatus] = useState(
    initialQuizState.goalStatus ?? ""
  );
  const levelUpTimeoutRef = useRef(null);
  const [userAnswers, setUserAnswers] = useState(() =>
    Array.isArray(initialQuizState.userAnswers)
      ? [...initialQuizState.userAnswers]
      : []
  );
  const [questionWithOptions, setQuestionWithOptions] = useState("");
  const [mathJaxStatus, setMathJaxStatus] = useState("loading");
  const [savedQuestions, setSavedQuestions] = useState(new Set()); // Track saved question IDs
  const [savingQuestionIds, setSavingQuestionIds] = useState(new Set()); // Track questions currently saving
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set()); // Track flagged question IDs
  const [showQuestionPalette, setShowQuestionPalette] = useState(false);
  const [visitedQuestions, setVisitedQuestions] = useState(new Set()); // Track visited question indices
  const questionPaletteRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const descriptiveTextareaRef = useRef(null);
  const descriptiveFileInputRef = useRef(null);
  const uploadDropdownRef = useRef(null);
  const cropperContainerRef = useRef(null);
  const levelUpPlayerRef = useRef(null);
  const clappingAudioRef = useRef(null);
  onCloseRef.current = onClose;

  const [questionList, setQuestionList] = useState([]);
  const [quizMetadata, setQuizMetadata] = useState(null);
  const [descriptiveAnswers, setDescriptiveAnswers] = useState(() =>
    Array.isArray(initialQuizState.descriptiveAnswers)
      ? [...initialQuizState.descriptiveAnswers]
      : []
  );
  const [descriptiveImages, setDescriptiveImages] = useState(() =>
    Array.isArray(initialQuizState.descriptiveUploads)
      ? initialQuizState.descriptiveUploads.map((group) =>
          Array.isArray(group) ? [...group] : []
        )
      : []
  );
  // Track whether user has taken a hint or checked answer for each question
  const [takenHintByQuestion, setTakenHintByQuestion] = useState([]);
  const [takenCheckAnswerByQuestion, setTakenCheckAnswerByQuestion] = useState(
    []
  );
  const [previewAttachmentState, setPreviewAttachmentState] = useState(null);
  const [previewAttachmentZoom, setPreviewAttachmentZoom] = useState(1);
  const [previewAttachmentPageInput, setPreviewAttachmentPageInput] =
    useState("1");
  const [imageToDelete, setImageToDelete] = useState(null);
  const [showImageDeleteConfirm, setShowImageDeleteConfirm] = useState(false);
  const [descriptiveStatusIndex, setDescriptiveStatusIndex] = useState(0);
  const previewAttachmentItems =
    previewAttachmentState?.items && Array.isArray(previewAttachmentState.items)
      ? previewAttachmentState.items
      : [];
  const previewAttachmentCount = previewAttachmentItems.length;
  const previewAttachmentIndex =
    previewAttachmentCount > 0
      ? clamp(previewAttachmentState?.index ?? 0, 0, previewAttachmentCount - 1)
      : 0;
  const previewAttachment =
    previewAttachmentCount > 0
      ? previewAttachmentItems[previewAttachmentIndex]
      : null;
  const disablePreviewPrev = previewAttachmentIndex <= 0;
  const disablePreviewNext =
    previewAttachmentIndex >= Math.max(previewAttachmentCount - 1, 0);
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [descriptiveEvaluations, setDescriptiveEvaluations] = useState([]);
  const [capturePreviewData, setCapturePreviewData] = useState(null);
  const [captureQuestionIndex, setCaptureQuestionIndex] = useState(null);
  const [captureCrop, setCaptureCrop] = useState({
    unit: "%",
    width: 90,
    height: 90,
    x: 5,
    y: 5,
  });
  const [captureRotation, setCaptureRotation] = useState(0);
  const [captureCompletedCrop, setCaptureCompletedCrop] = useState(null);
  const [captureImageDimensions, setCaptureImageDimensions] = useState(null);
  const [isProcessingCapturePreview, setIsProcessingCapturePreview] =
    useState(false);
  useEffect(() => {
    if (!previewAttachmentCount) {
      setPreviewAttachmentPageInput("1");
      return;
    }
    setPreviewAttachmentPageInput(String(previewAttachmentIndex + 1));
  }, [previewAttachmentCount, previewAttachmentIndex]);
  useEffect(() => {
    if (!previewAttachmentState) {
      return;
    }
    setPreviewAttachmentState((prev) => {
      if (!prev || !Array.isArray(prev.items) || prev.items.length === 0) {
        return prev;
      }
      const normalizedIndex = clamp(prev.index ?? 0, 0, prev.items.length - 1);
      if (normalizedIndex !== prev.index) {
        return { ...prev, index: normalizedIndex };
      }
      return prev;
    });
  }, [previewAttachmentCount, previewAttachmentState]);
  const openAttachmentPreview = useCallback((items, index = 0) => {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }
    const normalizedIndex = clamp(index, 0, items.length - 1);
    setPreviewAttachmentState({ items, index: normalizedIndex });
    setPreviewAttachmentZoom(1);
    setPreviewAttachmentPageInput(String(normalizedIndex + 1));
  }, []);
  const closeAttachmentPreview = useCallback(() => {
    setPreviewAttachmentState(null);
    setPreviewAttachmentZoom(1);
    setPreviewAttachmentPageInput("1");
  }, []);
  const goPrevPreviewAttachment = useCallback(() => {
    setPreviewAttachmentState((prev) => {
      if (!prev || !Array.isArray(prev.items) || prev.items.length <= 1) {
        return prev;
      }
      if ((prev.index ?? 0) <= 0) {
        return prev;
      }
      return { ...prev, index: (prev.index ?? 0) - 1 };
    });
  }, []);
  const goNextPreviewAttachment = useCallback(() => {
    setPreviewAttachmentState((prev) => {
      if (!prev || !Array.isArray(prev.items) || prev.items.length <= 1) {
        return prev;
      }
      const nextIndex = (prev.index ?? 0) + 1;
      if (nextIndex >= prev.items.length) {
        return prev;
      }
      return { ...prev, index: nextIndex };
    });
  }, []);
  const decreasePreviewAttachmentZoom = useCallback(() => {
    setPreviewAttachmentZoom((prev) =>
      Math.max(
        ATTACHMENT_PREVIEW_MIN_ZOOM,
        Number((prev - ATTACHMENT_PREVIEW_ZOOM_STEP).toFixed(2))
      )
    );
  }, []);
  const increasePreviewAttachmentZoom = useCallback(() => {
    setPreviewAttachmentZoom((prev) =>
      Math.min(
        ATTACHMENT_PREVIEW_MAX_ZOOM,
        Number((prev + ATTACHMENT_PREVIEW_ZOOM_STEP).toFixed(2))
      )
    );
  }, []);
  const commitPreviewAttachmentPage = useCallback(() => {
    if (!previewAttachmentState || previewAttachmentCount === 0) {
      setPreviewAttachmentPageInput("1");
      return;
    }
    const parsed = parseInt(previewAttachmentPageInput, 10);
    if (!Number.isFinite(parsed)) {
      setPreviewAttachmentPageInput(String(previewAttachmentIndex + 1));
      return;
    }
    const nextIndex = clamp(
      parsed - 1,
      0,
      Math.max(previewAttachmentCount - 1, 0)
    );
    setPreviewAttachmentState((prev) => {
      if (!prev) return prev;
      return { ...prev, index: nextIndex };
    });
  }, [
    previewAttachmentCount,
    previewAttachmentIndex,
    previewAttachmentPageInput,
    previewAttachmentState,
  ]);
  const handlePreviewAttachmentInputChange = useCallback((event) => {
    setPreviewAttachmentPageInput(event.target.value);
  }, []);
  const handlePreviewAttachmentInputBlur = useCallback(() => {
    commitPreviewAttachmentPage();
  }, [commitPreviewAttachmentPage]);
  const handlePreviewAttachmentInputKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commitPreviewAttachmentPage();
      }
    },
    [commitPreviewAttachmentPage]
  );
  const handlePreviewAttachmentOpenInNewTab = useCallback(() => {
    if (previewAttachment?.url) {
      window.open(previewAttachment.url, "_blank", "noopener,noreferrer");
    }
  }, [previewAttachment]);
  const triggerLevelUpCelebration = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!clappingAudioRef.current && typeof Audio !== "undefined") {
      const audio = new Audio("/clapping.mp3");
      audio.preload = "auto";
      clappingAudioRef.current = audio;
    }

    setShowLevelUpCelebration(true);

    const audio = clappingAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    }

    if (levelUpTimeoutRef.current) {
      clearTimeout(levelUpTimeoutRef.current);
    }
    levelUpTimeoutRef.current = window.setTimeout(() => {
      setShowLevelUpCelebration(false);
      levelUpTimeoutRef.current = null;

      if (clappingAudioRef.current) {
        clappingAudioRef.current.pause();
        clappingAudioRef.current.currentTime = 0;
      }
    }, LEVEL_UP_CELEBRATION_DURATION);
  }, []);

  // useEffect(() => {
  //   setDescriptiveAnswers((prev) => {
  //     const safePrev = Array.isArray(prev) ? [...prev] : [];
  //     if (safePrev.length >= questionList.length) {
  //       return safePrev;
  //     }
  //     const next = [...safePrev];
  //     while (next.length < questionList.length) {
  //       next.push("");
  //     }
  //     return next;
  //   });
  // }, [questionList.length]);

  // useEffect(() => {
  //   let mutated = false;
  //   setDescriptiveImages((prev) => {
  //     const safePrev = Array.isArray(prev) ? prev : [];
  //     if (safePrev.length >= questionList.length) {
  //       if (!Array.isArray(prev)) {
  //         mutated = true;
  //         return safePrev;
  //       }
  //       return prev;
  //     }
  //     const next = [...safePrev];
  //     while (next.length < questionList.length) {
  //       next.push([]);
  //     }
  //     mutated = true;
  //     return next;
  //   });
  //   if (mutated) {
  //     shouldPersistDescriptiveImagesRef.current = true;
  //   }
  // }, [questionList.length]);
  useEffect(() => {
    return () => {
      if (levelUpTimeoutRef.current) {
        clearTimeout(levelUpTimeoutRef.current);
        levelUpTimeoutRef.current = null;
      }
      if (clappingAudioRef.current) {
        clappingAudioRef.current.pause();
        clappingAudioRef.current.currentTime = 0;
        clappingAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!showLevelUpCelebration) {
      return;
    }
    const player = levelUpPlayerRef.current;
    if (!player) {
      return;
    }

    const handleReady = () => {
      try {
        const controller = player?.dotLottie;
        if (controller?.setLoop) {
          controller.setLoop(true);
        }
        if (controller?.setLoopCount) {
          controller.setLoopCount(0);
        }
        controller?.play?.();
      } catch (error) {
        console.error("Failed to start celebration animation:", error);
      }
    };

    player.addEventListener?.("ready", handleReady);
    if (player?.dotLottie) {
      handleReady();
    }

    return () => {
      player.removeEventListener?.("ready", handleReady);
    };
  }, [showLevelUpCelebration]);

  // Ensure hint/check-answer tracking arrays always cover all questions
  useEffect(() => {
    setTakenHintByQuestion((prev) => {
      const safePrev = Array.isArray(prev) ? [...prev] : [];
      while (safePrev.length < questionList.length) {
        safePrev.push(false);
      }
      return safePrev;
    });
    setTakenCheckAnswerByQuestion((prev) => {
      const safePrev = Array.isArray(prev) ? [...prev] : [];
      while (safePrev.length < questionList.length) {
        safePrev.push(false);
      }
      return safePrev;
    });
  }, [questionList.length]);

  function waitForMathJax() {
    return new Promise((resolve, reject) => {
      const resolveWhenReady = () => {
        const startupPromise = window.MathJax?.startup?.promise;
        if (startupPromise && typeof startupPromise.then === "function") {
          startupPromise
            .then(() => {
              resolve();
            })
            .catch(reject);
        } else {
          resolve();
        }
      };

      if (window.MathJax) return resolveWhenReady();

      // Poll until MathJax appears
      const id = setInterval(() => {
        if (window.MathJax) {
          clearInterval(id);
          resolveWhenReady();
        }
      }, 100);

      // Safety timeout
      setTimeout(() => {
        clearInterval(id);
        reject(new Error("MathJax not found on window within timeout"));
      }, 10000);
    });
  }
  useEffect(() => {
    let isMounted = true;
    setMathJaxStatus("loading");

    waitForMathJax()
      .then(() => {
        if (!isMounted) return;
        console.log("MathJAX loaded successfully.");
        setMathJaxStatus("ready");
        window.MathJax?.typesetPromise?.();
      })
      .catch((e) => {
        console.log("Error in loading MathJAX");
        console.error(e);
        toast.error("Error in rendering questions.");
        if (!isMounted) return;
        setMathJaxStatus("error");
      });

    return () => {
      isMounted = false;
    };
  }, []);
  // ======== Split screen + resizer state (only shown after Need Help) ========
  const [leftPanelWidth, setLeftPanelWidth] = useState(55); // %
  const resizeRef = useRef(null);
  const draggingRef = useRef(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  // NEW: gate the right panel/resizer visibility until user clicks Need Help
  const [showSidePanel, setShowSidePanel] = useState(false);
  const timerExpiredRef = useRef(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [selectedFlagReason, setSelectedFlagReason] = useState(
    FLAG_REASON_OPTIONS[0]
  );
  const [flagExplanation, setFlagExplanation] = useState("");
  const [isSubmittingFlag, setIsSubmittingFlag] = useState(false);
  const [isMasteryModeActive, setIsMasteryModeActive] = useState(
    isMasteryFlowActive()
  );

  // Heartbeat polling ref (for server-synced timer)
  const heartbeatIntervalRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const handleStorageUpdate = (event) => {
      if (event.key && event.key !== "masteryFlowActive") {
        return;
      }
      const active = isMasteryFlowActive();
      setIsMasteryModeActive(active);
      if (!active) {
        setShowSidePanel(false);
      }
    };

    window.addEventListener("storage", handleStorageUpdate);
    return () => window.removeEventListener("storage", handleStorageUpdate);
  }, []);

  useEffect(() => {
    if (isMasteryModeActive) {
      setShowSidePanel(false);
    }
  }, [isMasteryModeActive]);

  useEffect(() => {
    if (!quizCompleted) {
      return;
    }
    if (isMasteryFlowActive()) {
      deactivateMasteryFlow();
      setIsMasteryModeActive(false);
      setShowSidePanel(false);
    }
  }, [quizCompleted]);

  const handleMouseDown = (e) => {
    if (isMobile || !showSidePanel) return;
    draggingRef.current = true;
    e.preventDefault();
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!draggingRef.current || !resizeRef.current) return;
      const rect = resizeRef.current.getBoundingClientRect();
      const percent = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftPanelWidth(clamp(Math.round(percent), 20, 80));
    };
    const onMouseUp = () => {
      if (draggingRef.current) draggingRef.current = false;
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isMobile, showSidePanel]);
  // ==========================================================================

  // useEffect(() => {
  //   if (quizCompleted && embedded) {
  //     const timer = setTimeout(() => {
  //       onCloseRef.current?.();
  //     }, 5000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [quizCompleted, embedded]);

  const baseUrl = import.meta.env.VITE_APP_BASE_URL;

  const [loadedQuestions, setLoadedQuestions] = useState(false);
  const [initialQuestionBatch, setInitialQuestionBatch] = useState([]);
  const submittedQuestionIndices = useRef(new Set());

  useEffect(() => {
    console.log("Updating question list based on latest questions prop");
    setAllowUpdateQuestionList(false);
    const resolvedResultData =
      questions &&
      typeof questions === "object" &&
      !Array.isArray(questions) &&
      questions.result
        ? questions.result
        : questions;

    setQuizMetadata(resolvedResultData);

    const initialBatch = (() => {
      if (Array.isArray(resolvedResultData?.questions))
        return resolvedResultData.questions;
      if (resolvedResultData?.question) return [resolvedResultData.question];
      if (Array.isArray(resolvedResultData)) return resolvedResultData;
      if (Array.isArray(questions?.questions)) return questions.questions;
      if (questions?.question) return [questions.question];
      if (Array.isArray(questions)) return questions;
      return [];
    })();

    setInitialQuestionBatch(initialBatch);
    setQuestionList(normalizeQuestionsForDisplay(initialBatch));
    // setDescriptiveAnswers(
    //   Array.isArray(storedDescriptiveAnswers)
    //     ? storedDescriptiveAnswers.slice(0, initialBatch.length)
    //     : Array(initialBatch.length).fill("")
    // );
    // Initialize hint/check-answer tracking for initial questions
    setTakenHintByQuestion(Array(initialBatch.length).fill(false));
    setTakenCheckAnswerByQuestion(Array(initialBatch.length).fill(false));
    submittedQuestionIndices.current = new Set();
    setLoadedQuestions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  // useEffect(() => {
  //   if (skipStoredDescriptiveSyncRef.current) {
  //     skipStoredDescriptiveSyncRef.current = false;
  //     return;
  //   }
  //   if (!Array.isArray(storedDescriptiveAnswers)) {
  //     return;
  //   }
  //   setDescriptiveAnswers((prev) => {
  //     const targetLength = Math.max(
  //       prev.length,
  //       storedDescriptiveAnswers.length
  //     );
  //     if (targetLength === 0) return prev;
  //     const next = new Array(targetLength).fill("");
  //     for (let i = 0; i < targetLength; i += 1) {
  //       if (typeof storedDescriptiveAnswers[i] === "string") {
  //         next[i] = storedDescriptiveAnswers[i];
  //       } else {
  //         next[i] = prev[i] || "";
  //       }
  //     }
  //     return next;
  //   });
  // }, [storedDescriptiveAnswers]);

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const declaredQuestionLimit = useMemo(() => {
    const parsed = toNumber(quizMetadata?.question_count);
    return parsed !== null ? parsed : null;
  }, [quizMetadata?.question_count]);

  const questionCount = useMemo(() => {
    if (declaredQuestionLimit !== null) {
      return Math.max(declaredQuestionLimit, initialQuestionBatch.length);
    }
    return Math.max(questionList.length, 1);
  }, [declaredQuestionLimit, initialQuestionBatch.length, questionList.length]);

  const quizTimerSeconds = useMemo(() => {
    const parsed = toNumber(quizMetadata?.timer);
    return parsed !== null && parsed > 0 ? Math.floor(parsed) : null;
  }, [quizMetadata?.timer]);

  const extractedContent = useMemo(() => {
    const content = quizMetadata?.extracted_content;
    if (content && typeof content === "object") {
      return content;
    }
    return {};
  }, [quizMetadata]);

  const defaultSubject =
    quizMetadata?.subject ??
    extractedContent?.subject ??
    goalDetails?.subject ??
    "";
  const defaultGrade =
    goalDetails?.grade ??
    extractedContent?.grade ??
    quizMetadata?.grade ??
    goalDetails?.class ??
    "";

  const defaultTopic =
    quizMetadata?.topic ?? extractedContent?.topic ?? goalDetails?.topic ?? "";

  function normalizeTextValue(value) {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean")
      return String(value);
    if (Array.isArray(value)) {
      return value.map(normalizeTextValue).filter(Boolean).join(" ");
    }
    if (typeof value === "object") {
      const nestedKeys = [
        "option_text",
        "text",
        "label",
        "value",
        "content",
        "description",
        "prompt",
        "question",
        "rendered",
        "html",
      ];
      for (const key of nestedKeys) {
        if (key in value) {
          const resolved = normalizeTextValue(value[key]);
          if (resolved) return resolved;
        }
      }
      try {
        return JSON.stringify(value);
      } catch (error) {
        console.error("Failed to stringify text value", error);
        return "";
      }
    }
    return "";
  }

  const convertBreakTagsToNewlines = (value) => {
    if (typeof value !== "string") {
      return value;
    }
    return value.replace(/<br\s*\/?>/gi, "\n");
  };

  function extractOptions(question) {
    const getOptionText = (option) => {
      return normalizeTextValue(option);
    };

    const normalizeOptionsCollection = (collection) => {
      if (Array.isArray(collection)) {
        return collection.map(getOptionText).filter(Boolean);
      }
      if (collection && typeof collection === "object") {
        return Object.values(collection).map(getOptionText).filter(Boolean);
      }
      return [];
    };

    const primaryOptions = normalizeOptionsCollection(question?.options);
    if (primaryOptions.length) return primaryOptions;

    const choiceOptions = normalizeOptionsCollection(question?.choices);
    if (choiceOptions.length) return choiceOptions;

    const fallbackKeys = [
      "option_a",
      "option_b",
      "option_c",
      "option_d",
      "option_e",
      "option_f",
      "answer_a",
      "answer_b",
      "answer_c",
      "answer_d",
    ];
    const fallbackOptions = fallbackKeys
      .map((key) => getOptionText(question?.[key]))
      .filter(Boolean);

    return fallbackOptions;
  }

  function normalizeQuestionsForDisplay(questionItems) {
    if (!Array.isArray(questionItems)) return [];
    return questionItems.map((question) => ({
      ...question,
      _normalizedOptions: extractOptions(question),
    }));
  }

  const getNormalizedQuestionType = (question) => {
    const rawType = question?.question_type;
    if (typeof rawType === "string") {
      return rawType.trim().toLowerCase();
    }
    return "";
  };

  const isDescriptiveQuestionType = (question) => {
    const normalizedType = getNormalizedQuestionType(question);
    return DESCRIPTIVE_TYPES.includes(normalizedType);
  };

  const getDescriptiveConfig = (questionOrType) => {
    const configKey =
      typeof questionOrType === "string"
        ? questionOrType.trim().toLowerCase()
        : getNormalizedQuestionType(questionOrType);
    return DESCRIPTIVE_CONFIG[configKey] ?? DEFAULT_DESCRIPTIVE_CONFIG;
  };

  const resolveQuestionId = (question, fallbackId) =>
    question?.question_id || question?.id || question?.questionId || fallbackId;

  const resolveCorrectIndex = (question) =>
    typeof question?.correct_option_index === "number"
      ? question.correct_option_index
      : question?.correctAnswer;

  const postAnswerProgress = async (questionIndex) => {
    if (!Array.isArray(questionList) || !questionList.length) {
      return { nextQuestions: [], quizEnded: false };
    }
    const question = questionList[questionIndex];
    if (!question) {
      return { nextQuestions: [], quizEnded: false };
    }

    const isDescriptive = isDescriptiveQuestionType(question);
    let selectedIndex =
      typeof userAnswers[questionIndex] === "number"
        ? userAnswers[questionIndex]
        : typeof selectedOption === "number"
        ? selectedOption
        : null;
    const descriptiveAnswerText =
      currentQuestionIsDescriptive && descriptiveTextareaRef.current
        ? descriptiveTextareaRef.current.value
        : "";
    const hasDescriptiveImages =
      isDescriptive &&
      Array.isArray(descriptiveImages[questionIndex]) &&
      descriptiveImages[questionIndex].length > 0;

    if (isDescriptive) {
      if (!descriptiveAnswerText && !hasDescriptiveImages) {
        // toast.error(
        //   "Please enter your answer or upload at least one image before proceeding."
        // );
        return { nextQuestions: [], quizEnded: false };
      }
      selectedIndex = -1;
    } else if (typeof selectedIndex !== "number" || selectedIndex < 0) {
      return { nextQuestions: [], quizEnded: false };
    }

    const options = question?._normalizedOptions ?? extractOptions(question);
    const selectedOptionText = isDescriptive
      ? currentQuestionIsDescriptive && descriptiveTextareaRef.current
        ? descriptiveTextareaRef.current.value
        : selectedIndex >= 0 && selectedIndex < options.length
        ? options[selectedIndex]
        : ""
      : "";
    const correctIndex = isDescriptive ? -1 : resolveCorrectIndex(question);
    const correctOptionText = isDescriptive
      ? normalizeTextValue(
          question?.correct_answer ??
            question?.answer ??
            question?.expected_answer ??
            ""
        )
      : typeof correctIndex === "number" &&
        correctIndex >= 0 &&
        correctIndex < options.length
      ? options[correctIndex]
      : "";

    const payload = {
      attempt_id: quizMetadata?.attempt_id,
      question_id: resolveQuestionId(question, `question-${questionIndex + 1}`),
      option_index: selectedIndex,
      question_text: normalizeTextValue(
        question?.question_text ??
          question?.questionText ??
          question?.prompt ??
          ""
      ),
      selected_option_text: normalizeTextValue(selectedOptionText),
      correct_option_text: normalizeTextValue(correctOptionText),
      reasoning: normalizeTextValue(
        question?.resoning ?? question?.reasoning ?? question?.explanation ?? ""
      ),
      subject: question?.subject ?? defaultSubject ?? "",
      topic: question?.topic ?? question?.subtopic ?? defaultTopic ?? "",
      source_version: question?.source_version ?? 1,
      // Whether user took a hint or checked answer for this question
      taken_hint: Boolean(takenHintByQuestion[questionIndex]),
      taken_check_answer: Boolean(takenCheckAnswerByQuestion[questionIndex]),
    };

    // For descriptive questions, add the evaluation response as JSON string if available
    if (
      isDescriptive &&
      Array.isArray(descriptiveEvaluations) &&
      descriptiveEvaluations[questionIndex]
    ) {
      try {
        payload.evaluated = JSON.stringify(
          descriptiveEvaluations[questionIndex]
        );
      } catch (error) {
        console.error("Failed to stringify evaluation:", error);
      }
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please sign in to continue the quiz.");
      throw new Error("Authentication token is missing.");
    }

    try {
      const isDescriptiveWithImages = isDescriptive && hasDescriptiveImages;

      let requestBody;
      let requestHeaders = {
        Authorization: `Bearer ${token}`,
      };

      // Always send multipart/form-data so backend can consistently accept files,
      // even when there are no descriptive images.
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        formData.append(key, String(value));
      });

      if (isDescriptiveWithImages) {
        descriptiveImages[questionIndex].forEach((img) => {
          if (img?.file) {
            formData.append("answer_images", img.file);
          }
        });
      }

      requestBody = formData;

      const response = await fetch(`${baseUrl}/v2/quiz/submit-answer-new`, {
        method: "POST",
        headers: requestHeaders,
        body: requestBody,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText
            ? `Failed to submit answer: ${errorText}`
            : `Failed to submit answer (status ${response.status}).`
        );
      }

      const responseText = await response.text();
      let parsedData = {};
      if (responseText) {
        try {
          parsedData = JSON.parse(responseText);
        } catch (parseError) {
          console.error("Failed to parse submit-answer response:", parseError);
          throw new Error("Received invalid JSON from the quiz service.");
        }
      }

      const parsedResult =
        parsedData?.result && typeof parsedData.result === "object"
          ? parsedData.result
          : parsedData;

      const {
        question: singleQuestion,
        questions: nextQuestionBatch,
        next_question: nextQuestionSingle, // backend may send this instead of question(s)
        next_questions: nextQuestionsArray, // or this (plural)
        is_correct,
        correct_option_text,
        correct_answer,
        explanation,
        check_answer_text,
        message,
        success,
        quizEnded,
        ...metadataUpdates
      } = parsedResult || {};
      console.log("quiz/submit-answer-new quizEnded -->", quizEnded);

      if (metadataUpdates && Object.keys(metadataUpdates).length > 0) {
        setQuizMetadata((prev) => ({ ...prev, ...metadataUpdates }));
      }
      const loChangedRaw =
        metadataUpdates?.isLoChanged ?? parsedResult?.isLoChanged ?? null;
      const loChanged =
        loChangedRaw === true ||
        loChangedRaw === "true" ||
        loChangedRaw === 1 ||
        loChangedRaw === "1";
      if (loChanged) {
        triggerLevelUpCelebration();
      }

      // For descriptive questions, persist evaluation details for this question index
      // so that "Check Answer" works when revisiting the question.
      const evaluationPatch = {};
      const rawEvaluation =
        parsedResult?.evaluation && typeof parsedResult.evaluation === "object"
          ? parsedResult.evaluation
          : null;
      if (typeof is_correct === "boolean") {
        evaluationPatch.is_correct = is_correct;
      }
      const resolvedCorrectText =
        normalizeTextValue(
          correct_option_text ??
            correct_answer ??
            parsedResult?.correct_option_text ??
            parsedResult?.correct_answer ??
            ""
        ) || "";
      if (resolvedCorrectText) {
        evaluationPatch.correct_answer = resolvedCorrectText;
      }
      const resolvedExplanation =
        normalizeTextValue(
          check_answer_text ??
            explanation ??
            parsedResult?.check_answer_text ??
            parsedResult?.explanation ??
            rawEvaluation?.feedback ??
            ""
        ) || "";
      if (resolvedExplanation) {
        evaluationPatch.check_answer_text = resolvedExplanation;
      }

      const normalizedEvaluation =
        normalizeDescriptiveEvaluation(rawEvaluation);
      if (normalizedEvaluation) {
        evaluationPatch.descriptiveEvaluation = normalizedEvaluation;
        if (
          typeof evaluationPatch.is_correct !== "boolean" &&
          typeof normalizedEvaluation.is_correct === "boolean"
        ) {
          evaluationPatch.is_correct = normalizedEvaluation.is_correct;
        }
        if (
          !evaluationPatch.check_answer_text &&
          normalizedEvaluation.feedback
        ) {
          evaluationPatch.check_answer_text = normalizeTextValue(
            normalizedEvaluation.feedback
          );
        }
      }

      if (Object.keys(evaluationPatch).length > 0) {
        setQuestionList((prev) => {
          if (!Array.isArray(prev) || !prev[questionIndex]) return prev;
          const next = [...prev];
          next[questionIndex] = {
            ...prev[questionIndex],
            ...evaluationPatch,
          };
          return next;
        });
      }

      // Normalize any of: questions, next_questions, next_question, or question
      const nextQuestions = Array.isArray(nextQuestionBatch)
        ? nextQuestionBatch
        : Array.isArray(nextQuestionsArray)
        ? nextQuestionsArray
        : nextQuestionSingle
        ? [nextQuestionSingle]
        : singleQuestion
        ? [singleQuestion]
        : [];

      // Treat quizEnded=true from backend as authoritative ONLY when no next question is provided.
      // If backend sends both quizEnded=true and a next question, continue the quiz.
      const effectiveQuizEnded =
        quizEnded === true && nextQuestions.length === 0;
      descriptiveTextareaRef.current
        ? (descriptiveTextareaRef.current.value = "")
        : null;

      return {
        nextQuestions,
        quizEnded: effectiveQuizEnded,
      };
    } catch (error) {
      console.error("Failed to post answer progress:", error);
      throw error;
    }
  };

  const [remainingTime, setRemainingTime] = useState(null);

  useEffect(() => {
    if (!questionCount && !initialQuestionBatch.length) {
      return;
    }

    const fallbackCount = Math.max(
      questionCount,
      initialQuestionBatch.length,
      1
    );
    const total =
      typeof quizTimerSeconds === "number" && quizTimerSeconds > 0
        ? quizTimerSeconds
        : fallbackCount * 60;

    timerExpiredRef.current = false;
    setRemainingTime(total);
  }, [topicId, questionCount, quizTimerSeconds, initialQuestionBatch.length]);

  // Heartbeat: keep timer in sync with backend every ~40 seconds once quiz is running.
  useEffect(() => {
    // Conditions to start heartbeat:
    // - quiz has started (we have questions)
    // - quiz is not completed
    if (
      quizCompleted ||
      !Array.isArray(questionList) ||
      questionList.length === 0
    ) {
      return undefined;
    }

    // Avoid multiple intervals
    if (heartbeatIntervalRef.current !== null) {
      return undefined;
    }

    const sendHeartbeat = async () => {
      if (!baseUrl) return;
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch(`${baseUrl}/v2/quiz/heartbeat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            attempt_id: quizMetadata?.attempt_id ?? undefined,
          }),
        });

        // Handle explicit "quiz abandoned/expired" style errors
        if (!response.ok) {
          let errorPayload = {};
          try {
            errorPayload = await response.json();
          } catch {
            // ignore JSON parse failure here
          }

          const errorCode = errorPayload?.error;

          if (
            response.status === 410 ||
            errorCode === "QUIZ_ABANDONED" ||
            errorCode === "QUIZ_EXPIRED"
          ) {
            // Stop heartbeat
            if (heartbeatIntervalRef.current !== null) {
              clearInterval(heartbeatIntervalRef.current);
              heartbeatIntervalRef.current = null;
            }

            if (errorCode === "QUIZ_ABANDONED") {
              toast.error("This quiz attempt has been abandoned.");
            } else if (errorCode === "QUIZ_EXPIRED") {
              toast.error("This quiz has expired.");
            }

            // End quiz flow using existing results flow
            await completeQuiz();
          }

          return;
        }

        const data = await response.json();
        const backendRemaining = Number(data?.remainingTimeSeconds);
        if (Number.isFinite(backendRemaining) && backendRemaining >= 0) {
          setRemainingTime(Math.floor(backendRemaining));
        }
      } catch (error) {
        console.error("Heartbeat error:", error);
        // On network or other errors, keep local timer running; no extra handling needed.
      }
    };

    // Send one heartbeat immediately after quiz has started, then every 40 seconds.
    void sendHeartbeat();
    heartbeatIntervalRef.current = window.setInterval(sendHeartbeat, 42000);

    // Cleanup when dependencies change or component unmounts
    return () => {
      if (heartbeatIntervalRef.current !== null) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [quizCompleted, questionList, baseUrl, quizMetadata?.attempt_id]);

  useEffect(() => {
    if (remainingTime === null) return;
    if (quizCompleted) return;
    if (remainingTime <= 0) return;

    const id = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev === null) return prev;
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [remainingTime, quizCompleted]);

  useEffect(() => {
    if (remainingTime === null) return;
    if (quizCompleted) return;
    if (remainingTime > 0) return;
    if (timerExpiredRef.current) return;

    timerExpiredRef.current = true;
    void completeQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingTime, quizCompleted]);

  const formatTime = (totalSeconds) => {
    if (totalSeconds === null || totalSeconds === undefined) {
      return "--:--";
    }
    const safeTotal = Math.max(0, Math.floor(totalSeconds));
    const m = Math.floor(safeTotal / 60)
      .toString()
      .padStart(2, "0");
    const s = (safeTotal % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  useEffect(() => {
    if (embedded) return;
    if (!questionCount || storedTopicId !== topicId) {
      navigate("/", {
        state: { error: "No active quiz found. Please start a goal." },
      });
    }
  }, [embedded, navigate, questionCount, storedTopicId, topicId]);

  const handleOptionSelect = (index) => {
    // Do not allow changes on descriptive questions or on already-submitted questions
    if (
      isDescriptiveQuestionType(questionList[currentQuestionIndex]) ||
      submittedQuestionIndices.current.has(currentQuestionIndex)
    ) {
      return;
    }
    // Mark current question as visited when user selects an option
    setVisitedQuestions((prev) => new Set([...prev, currentQuestionIndex]));

    const updatedAnswers = Array.isArray(userAnswers) ? [...userAnswers] : [];
    updatedAnswers[currentQuestionIndex] = index;
    setSelectedOption(index);
    setUserAnswers(updatedAnswers);
  };

  // const handleDescriptiveAnswerChange = (index, value) => {
  //   // Do not allow editing descriptive answers for already-submitted questions
  //   if (submittedQuestionIndices.current.has(index)) {
  //     return;
  //   }
  //   setVisitedQuestions((prev) => new Set([...prev, index]));
  //   shouldPersistDescriptiveRef.current = true;
  //   setDescriptiveAnswers((prev) => {
  //     const next = Array.isArray(prev) ? [...prev] : [];
  //     next[index] = value;
  //     return next;
  //   });
  // };

  const handleDescriptiveImagesUpload = (questionIndex, fileList) => {
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);
    if (!files.length) return;

    const timestamp = Date.now();

    setDescriptiveImages((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      while (next.length <= questionIndex) {
        next.push([]);
      }

      const existingForQuestion = Array.isArray(next[questionIndex])
        ? [...next[questionIndex]]
        : [];

      const newImages = files.map((file, idx) => {
        const mimeType =
          typeof file?.type === "string" ? file.type.toLowerCase() : "";
        const fileName =
          typeof file?.name === "string" && file.name.trim().length > 0
            ? file.name
            : `file-${timestamp}-${idx + 1}`;
        const isPdf =
          mimeType === "application/pdf" ||
          fileName.toLowerCase().endsWith(".pdf");
        return {
          id: `${timestamp}-${questionIndex}-${idx}-${Math.random()
            .toString(36)
            .slice(2, 8)}`,
          file,
          name: fileName,
          url: URL.createObjectURL(file),
          type: mimeType,
          isPdf,
        };
      });

      next[questionIndex] = [...existingForQuestion, ...newImages];
      return next;
    });
  };

  const openImageDeleteConfirm = (questionIndex, imageId) => {
    setImageToDelete({ questionIndex, imageId });
    setShowImageDeleteConfirm(true);
  };

  const handleConfirmDeleteImage = () => {
    if (!imageToDelete) return;
    const { questionIndex, imageId } = imageToDelete;

    setDescriptiveImages((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      if (!Array.isArray(next[questionIndex])) return next;

      const imagesForQuestion = [...next[questionIndex]];
      const idx = imagesForQuestion.findIndex((img) => img.id === imageId);

      if (idx !== -1) {
        const [removed] = imagesForQuestion.splice(idx, 1);
        if (removed?.url) {
          URL.revokeObjectURL(removed.url);
        }
      }

      next[questionIndex] = imagesForQuestion;
      return next;
    });

    setImageToDelete(null);
    setShowImageDeleteConfirm(false);
  };

  const handleCancelDeleteImage = () => {
    setImageToDelete(null);
    setShowImageDeleteConfirm(false);
  };

  // Camera modal handlers
  useEffect(() => {
    if (showCameraModal && videoRef.current) {
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: "user" } })
        .then((stream) => {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch((err) => {
          console.error("Error accessing camera:", err);
          toast.error("Failed to access camera. Please check permissions.");
          setShowCameraModal(false);
        });
    } else if (!showCameraModal && streamRef.current) {
      // Clean up stream when modal closes
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [showCameraModal]);

  useEffect(() => {
    return () => {
      if (capturePreviewData?.url) {
        URL.revokeObjectURL(capturePreviewData.url);
      }
    };
  }, [capturePreviewData]);

  useEffect(() => {
    if (capturePreviewData) {
      setCaptureCrop({ unit: "%", width: 90, height: 90, x: 5, y: 5 });
      setCaptureCompletedCrop(null);
      setCaptureImageDimensions(null);
    }
  }, [capturePreviewData]);

  const resetCapturePreviewState = () => {
    setCapturePreviewData(null);
    setCaptureQuestionIndex(null);
    setCaptureCrop({ unit: "%", width: 90, height: 90, x: 5, y: 5 });
    setCaptureRotation(0);
    setCaptureCompletedCrop(null);
    setCaptureImageDimensions(null);
    setIsProcessingCapturePreview(false);
  };

  const handleCancelCapturePreview = () => {
    resetCapturePreviewState();
  };

  const handleConfirmCapturePreview = async () => {
    if (!capturePreviewData?.url) {
      return;
    }

    if (
      !captureCompletedCrop ||
      !captureImageDimensions ||
      !captureImageDimensions.renderedWidth ||
      !captureImageDimensions.renderedHeight ||
      captureCompletedCrop.width <= 0 ||
      captureCompletedCrop.height <= 0
    ) {
      toast.error(
        "Preview is still initializing. Please wait a moment before uploading."
      );
      return;
    }

    const normalizedRotation = ((captureRotation % 360) + 360) % 360 || 0;

    setIsProcessingCapturePreview(true);
    try {
      const { renderedWidth, renderedHeight, naturalWidth, naturalHeight } =
        captureImageDimensions;

      const pixelCrop = convertToPixelCrop(
        captureCompletedCrop,
        renderedWidth,
        renderedHeight
      );

      const scaleX =
        naturalWidth && renderedWidth ? naturalWidth / renderedWidth : 1;
      const scaleY =
        naturalHeight && renderedHeight ? naturalHeight / renderedHeight : 1;

      const scaledPixelCrop = {
        x: pixelCrop.x * scaleX,
        y: pixelCrop.y * scaleY,
        width: pixelCrop.width * scaleX,
        height: pixelCrop.height * scaleY,
      };

      const croppedBlob = await getCroppedImageBlob(
        capturePreviewData.url,
        scaledPixelCrop,
        normalizedRotation
      );
      if (!croppedBlob) {
        throw new Error("Cropped blob is empty");
      }

      const targetQuestionIndex =
        typeof captureQuestionIndex === "number" && captureQuestionIndex >= 0
          ? captureQuestionIndex
          : 0;
      const finalFile = new File(
        [croppedBlob],
        capturePreviewData.file?.name || `camera-capture-${Date.now()}.jpg`,
        {
          type: capturePreviewData.file?.type || "image/jpeg",
        }
      );

      handleDescriptiveImagesUpload(targetQuestionIndex, [finalFile]);
      toast.success("Captured photo added. You can submit the answer now.");
      resetCapturePreviewState();
    } catch (error) {
      console.error("Error processing captured image:", error);
      toast.error("Could not process the captured image. Please try again.");
      setIsProcessingCapturePreview(false);
    }
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      toast.error("Failed to access the camera context.");
      return;
    }
    ctx.drawImage(videoRef.current, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error("Failed to capture image");
          return;
        }

        const timestamp = Date.now();
        const fileName = `camera-capture-${timestamp}.jpg`;
        const file = new File([blob], fileName, { type: "image/jpeg" });

        const previewUrl = URL.createObjectURL(file);

        const questionIndex =
          typeof currentQuestionIndex === "number" && currentQuestionIndex >= 0
            ? currentQuestionIndex
            : 0;

        setCaptureQuestionIndex(questionIndex);
        setCaptureCrop({ unit: "%", width: 90, height: 90, x: 5, y: 5 });
        setCaptureRotation(0);
        setCaptureCompletedCrop(null);
        setCaptureImageDimensions(null);
        setCapturePreviewData({
          file,
          url: previewUrl,
        });

        setShowCameraModal(false);
      },
      "image/jpeg",
      0.9
    );
  };

  const handleCloseCameraModal = () => {
    setShowCameraModal(false);
  };

  // useEffect(() => {
  //   if (!shouldPersistDescriptiveRef.current) {
  //     console.log(
  //       "[GoalQuestions] descriptiveAnswers useEffect skipped (persist flag not set)"
  //     );
  //     return;
  //   }
  //   shouldPersistDescriptiveRef.current = false;
  //   console.log(
  //     "[GoalQuestions] descriptiveAnswers useEffect triggered (persistence disabled)"
  //   );
  // }, [descriptiveAnswers]);

  // useEffect(() => {
  //   if (!shouldPersistDescriptiveImagesRef.current) {
  //     console.log(
  //       "[GoalQuestions] descriptiveImages useEffect skipped (persist flag not set)"
  //     );
  //     return;
  //   }
  //   shouldPersistDescriptiveImagesRef.current = false;
  //   console.log(
  //     "[GoalQuestions] descriptiveImages useEffect triggered (persistence disabled)"
  //   );
  // }, [descriptiveImages]);

  useEffect(() => {
    // Mark current question as visited when question index changes
    if (currentQuestionIndex >= 0) {
      setVisitedQuestions((prev) => new Set([...prev, currentQuestionIndex]));
    }
    // Reset hint visibility when moving to a different question
    setShowHint(false);

    const currentQuestion = questionList[currentQuestionIndex];
    if (isDescriptiveQuestionType(currentQuestion)) {
      if (selectedOption !== null) {
        setSelectedOption(null);
      }
      return;
    }

    const existingSelection =
      typeof userAnswers[currentQuestionIndex] === "number"
        ? userAnswers[currentQuestionIndex]
        : null;
    if (existingSelection !== null && selectedOption !== existingSelection) {
      setSelectedOption(existingSelection);
    }
  }, [currentQuestionIndex, questionList, selectedOption, userAnswers]);

  const [submittingAnswer, setSubmitAnswer] = useState(false);

  const [allowUpdateQuestionList, setAllowUpdateQuestionList] = useState(false);
  const [showViewResult, setShowViewResult] = useState(false);
  const handleNextQuestion = async () => {
    if (!questionList.length) return;
    setShowViewResult(true);
    const descriptiveResponseText =
      currentQuestionIsDescriptive && descriptiveTextareaRef.current
        ? descriptiveTextareaRef.current.value
        : "";
    console.log(descriptiveResponseText);
    // if (currentQuestionIsDescriptive) {
    //   shouldPersistDescriptiveRef.current = true;
    //   setDescriptiveAnswers((prev) => {
    //     const next = Array.isArray(prev) ? [...prev] : [];
    //     next[currentQuestionIndex] = descriptiveResponseText;
    //     return next;
    //   });
    // }
    setSubmitAnswer(true);
    setAllowUpdateQuestionList(true);

    setVisitedQuestions((prev) => new Set([...prev, currentQuestionIndex]));

    const hasAlreadySubmitted =
      submittedQuestionIndices.current.has(currentQuestionIndex);
    let nextQuestions = [];
    let quizEnded = false;

    try {
      if (!hasAlreadySubmitted) {
        const result = await postAnswerProgress(currentQuestionIndex);
        nextQuestions = Array.isArray(result?.nextQuestions)
          ? result.nextQuestions
          : [];
        quizEnded = result?.quizEnded === true;
        submittedQuestionIndices.current.add(currentQuestionIndex);
      }
    } catch (error) {
      if (error?.response?.status === 503) {
        toast.error(
          "Our tutors are busy with other students. Please try again in a few minutes."
        );
      } else {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to submit your answer. Please try again."
        );
      }
      setSubmitAnswer(false);
      return;
    }

    const answeredSoFar = currentQuestionIndex + 1;
    const hasExistingNextQuestion =
      questionList.length > currentQuestionIndex + 1;
    const willHaveNextQuestion =
      hasExistingNextQuestion || nextQuestions.length > 0;

    // Only enforce declaredQuestionLimit when the backend is not providing any further questions.
    const reachedDeclaredLimit =
      declaredQuestionLimit !== null &&
      answeredSoFar >= declaredQuestionLimit &&
      !willHaveNextQuestion;

    if (reachedDeclaredLimit || quizEnded || !willHaveNextQuestion) {
      await completeQuiz();
      return;
    }

    if (nextQuestions.length > 0) {
      setQuestionList((prev) => [
        ...prev,
        ...normalizeQuestionsForDisplay(nextQuestions),
      ]);
    }

    const nextQuestionIndex = currentQuestionIndex + 1;
    const nextSelected =
      typeof userAnswers[nextQuestionIndex] === "number"
        ? userAnswers[nextQuestionIndex]
        : null;

    setShowExplanation(false);
    setSelectedOption(nextSelected);
    setCurrentQuestionIndex(nextQuestionIndex);
    // Small delay to show loading state
    await new Promise((resolve) => setTimeout(resolve, 300));
    setSubmitAnswer(false);
    if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise();
    }
  };

  const handleSkipQuestion = async () => {
    if (!questionList.length) return;
    if (currentQuestionIndex >= questionList.length - 1) {
      return;
    }
    setVisitedQuestions((prev) => new Set([...prev, currentQuestionIndex]));
    const nextQuestionIndex = currentQuestionIndex + 1;
    const nextSelected =
      typeof userAnswers[nextQuestionIndex] === "number"
        ? userAnswers[nextQuestionIndex]
        : null;
    setShowExplanation(false);
    setSelectedOption(nextSelected);
    setCurrentQuestionIndex(nextQuestionIndex);
    setSubmitAnswer(false);
    if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise();
    }
  };

  const handlePreviousQuestion = async () => {
    if (currentQuestionIndex <= 0) return;
    setSubmitAnswer(true);
    const previousQuestionIndex = currentQuestionIndex - 1;
    setVisitedQuestions((prev) => new Set([...prev, previousQuestionIndex]));
    const previousSelected =
      typeof userAnswers[previousQuestionIndex] === "number"
        ? userAnswers[previousQuestionIndex]
        : null;
    setShowExplanation(false);
    setSelectedOption(previousSelected);
    setCurrentQuestionIndex(previousQuestionIndex);
    // Small delay to show loading state
    await new Promise((resolve) => setTimeout(resolve, 300));
    setSubmitAnswer(false);
    if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise();
    }
  };

  const handleCheckAnswer = async () => {
    const descriptiveResponseText =
      currentQuestionIsDescriptive && descriptiveTextareaRef.current
        ? descriptiveTextareaRef.current.value
        : "";
    if (isMasteryModeActive) {
      return;
    }

    // Mark that "Check Answer" has been taken for this question
    setTakenCheckAnswerByQuestion((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      while (next.length <= normalizedQuestionIndex) {
        next.push(false);
      }
      next[normalizedQuestionIndex] = true;
      return next;
    });

    // Descriptive questions: call dedicated descriptive evaluation API
    if (currentQuestionIsDescriptive && questionIsReady) {
      // If we already have evaluation stored for this question, just show it
      if (hasDescriptiveEvaluation) {
        setShowExplanation(true);
        return;
      }
      setIsEvaluatingScore(true);
      setSubmitAnswer(true);
      const questionIndex = normalizedQuestionIndex;
      const question = currentQuestionData;

      const descriptiveAnswerText =
        currentQuestionIsDescriptive && descriptiveTextareaRef.current
          ? descriptiveTextareaRef.current.value
          : "";
      const hasImages = hasDescriptiveImages;

      // Require at least some content (text or images)
      if (!descriptiveTextareaRef.current.value && !hasImages) {
        return;
      }

      if (!baseUrl) {
        console.error(
          "VITE_APP_BASE_URL is not configured. Cannot evaluate descriptive answer."
        );
        toast.error(
          "Unable to evaluate your answer right now. Please try again later."
        );
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please sign in to check your answer.");
        return;
      }

      const payload = {
        attempt_id: quizMetadata?.attempt_id,
        question_id: resolveQuestionId(
          question,
          `question-${questionIndex + 1}`
        ),
        option_index: -1,
        question_text: normalizeTextValue(
          question?.question_text ??
            question?.questionText ??
            question?.prompt ??
            ""
        ),
        selected_option_text: normalizeTextValue(descriptiveAnswerText),
        correct_option_text: "",
        reasoning: normalizeTextValue(
          question?.resoning ??
            question?.reasoning ??
            question?.explanation ??
            ""
        ),
        subject: question?.subject ?? defaultSubject ?? "",
        topic: question?.topic ?? question?.subtopic ?? defaultTopic ?? "",
        source_version: question?.source_version ?? 1,
      };

      try {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          formData.append(key, String(value));
        });

        if (hasImages) {
          descriptiveImages[questionIndex].forEach((img) => {
            if (img?.file) {
              formData.append("answer_images", img.file);
            }
          });
        }

        const response = await fetch(`${baseUrl}/v2/quiz/descriptive-eval`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            errorText
              ? `Failed to evaluate answer: ${errorText}`
              : `Failed to evaluate answer (status ${response.status}).`
          );
        }

        const responseText = await response.text();
        let parsedData = {};
        if (responseText) {
          try {
            parsedData = JSON.parse(responseText);
          } catch (parseError) {
            console.error(
              "Failed to parse descriptive-eval response:",
              parseError
            );
            throw new Error("Received invalid JSON from the quiz service.");
          }
        }

        const parsedResult =
          parsedData?.result && typeof parsedData.result === "object"
            ? parsedData.result
            : parsedData;

        // Store the entire evaluation response for later use in submit-answer-new
        setDescriptiveEvaluations((prev) => {
          const next = Array.isArray(prev) ? [...prev] : [];
          while (next.length <= questionIndex) {
            next.push(null);
          }
          next[questionIndex] = parsedResult;
          return next;
        });

        const {
          is_correct,
          correct_option_text,
          correct_answer,
          explanation,
          check_answer_text,
        } = parsedResult || {};

        // Build evaluation patch (mirrors descriptive handling in submit-answer)
        const evaluationPatch = {};
        const rawEvaluation =
          parsedResult?.evaluation &&
          typeof parsedResult.evaluation === "object"
            ? parsedResult.evaluation
            : null;

        if (typeof is_correct === "boolean") {
          evaluationPatch.is_correct = is_correct;
        }

        const resolvedCorrectText =
          normalizeTextValue(
            correct_option_text ??
              correct_answer ??
              parsedResult?.correct_option_text ??
              parsedResult?.correct_answer ??
              ""
          ) || "";
        if (resolvedCorrectText) {
          evaluationPatch.correct_answer = resolvedCorrectText;
        }

        const resolvedExplanation =
          normalizeTextValue(
            check_answer_text ??
              explanation ??
              parsedResult?.check_answer_text ??
              parsedResult?.explanation ??
              rawEvaluation?.feedback ??
              ""
          ) || "";
        if (resolvedExplanation) {
          evaluationPatch.check_answer_text = resolvedExplanation;
        }

        const normalizedEvaluation =
          normalizeDescriptiveEvaluation(rawEvaluation);
        if (normalizedEvaluation) {
          evaluationPatch.descriptiveEvaluation = normalizedEvaluation;
          if (
            typeof evaluationPatch.is_correct !== "boolean" &&
            typeof normalizedEvaluation.is_correct === "boolean"
          ) {
            evaluationPatch.is_correct = normalizedEvaluation.is_correct;
          }
          if (
            !evaluationPatch.check_answer_text &&
            normalizedEvaluation.feedback
          ) {
            evaluationPatch.check_answer_text = normalizeTextValue(
              normalizedEvaluation.feedback
            );
          }
        }

        if (Object.keys(evaluationPatch).length > 0) {
          setQuestionList((prev) => {
            if (!Array.isArray(prev) || !prev[questionIndex]) return prev;
            const next = [...prev];
            next[questionIndex] = {
              ...prev[questionIndex],
              ...evaluationPatch,
            };
            return next;
          });
        }
        setIsEvaluatingScore(false);
        // Show the descriptive evaluation/explanation block
        setShowExplanation(true);
      } catch (error) {
        console.error("Failed to evaluate descriptive answer:", error);
        if (error?.response?.status === 503) {
          toast.error(
            "Our tutors are busy with other students. Please try again in a few minutes."
          );
        } else {
          toast.error("Failed to evaluate your answer. Please try again.");
        }
      } finally {
        setSubmitAnswer(false);
        setIsEvaluatingScore(false);
      }

      return;
    }

    // Objective questions: just reveal explanation using existing data
    setShowExplanation(true);
  };

  const handleOpenFlagModal = () => {
    setSelectedFlagReason((prev) => prev || FLAG_REASON_OPTIONS[0]);
    setFlagExplanation("");
    setShowFlagModal(true);
  };

  const handleCloseFlagModal = () => {
    setShowFlagModal(false);
    setFlagExplanation("");
    setSelectedFlagReason(FLAG_REASON_OPTIONS[0]);
  };

  const handleSubmitFlag = async () => {
    if (!baseUrl) {
      console.error(
        "VITE_APP_BASE_URL is not configured. Cannot submit flag request."
      );
      toast.error("Unable to submit flag request. Please try again later.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please sign in to flag a question.");
      return;
    }

    if (!currentQuestionData) {
      toast.error("Please wait for the question to load before flagging.");
      return;
    }

    const questionId = resolveQuestionId(
      currentQuestionData,
      `question-${normalizedQuestionIndex + 1}`
    );

    if (!questionId) {
      toast.error("Unable to determine the question details.");
      return;
    }

    const payload = {
      question_id: questionId,
      attempt_id: quizMetadata?.attempt_id || "",
      subject: currentQuestionData?.subject ?? defaultSubject ?? "",
      chapter:
        currentQuestionData?.topic ??
        currentQuestionData?.chapter ??
        currentQuestionData?.subtopic ??
        defaultTopic ??
        "",
      grade: defaultGrade || "",
      flag_type: selectedFlagReason,
      reason: flagExplanation.trim() || selectedFlagReason,
      audit_timestamp: new Date().toISOString(),
    };

    setIsSubmittingFlag(true);
    try {
      const response = await axios.post(
        `${baseUrl}/v2/flag-questions`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success("Thanks! Your feedback has been recorded.");
      }
      setFlaggedQuestions((prev) => {
        const next = new Set(prev);
        next.add(questionId);
        return next;
      });
      handleCloseFlagModal();
    } catch (error) {
      console.error("Failed to submit flag request:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to submit feedback. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmittingFlag(false);
    }
  };

  const handleSaveQuestion = async (
    questionDataParam = currentQuestionData,
    questionIndexParam = normalizedQuestionIndex
  ) => {
    let questionData = questionDataParam ?? currentQuestionData;
    let targetQuestionIndex =
      typeof questionIndexParam === "number"
        ? questionIndexParam
        : normalizedQuestionIndex;

    if (
      questionData &&
      typeof questionData === "object" &&
      typeof questionData.preventDefault === "function"
    ) {
      // Called directly as an event handler; normalize to the current question.
      questionData.preventDefault();
      questionData = currentQuestionData;
      targetQuestionIndex = normalizedQuestionIndex;
    }

    if (!questionData) return;

    const fallbackIndex =
      typeof targetQuestionIndex === "number"
        ? targetQuestionIndex
        : normalizedQuestionIndex;
    const questionId = resolveQuestionId(
      questionData,
      `question-${fallbackIndex + 1}`
    );

    // Check if already saved or currently saving
    if (savedQuestions.has(questionId) || savingQuestionIds.has(questionId)) {
      return;
    }

    setSavingQuestionIds((prev) => {
      const next = new Set(prev);
      next.add(questionId);
      return next;
    });
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication required to save questions");
        return;
      }

      const payload = {
        question_id: questionId,
        attempt_id: quizMetadata?.attempt_id || "",
        subject: questionData?.subject ?? defaultSubject ?? "",
        chapter:
          questionData?.topic ?? questionData?.subtopic ?? defaultTopic ?? "",
      };

      const response = await axios.post(
        `${baseUrl}/v2/quiz/mark-question`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Treat any successful 2xx response as a successful save unless the
      // backend explicitly returns success === false.
      const { success, message } = response.data || {};
      if (success === false) {
        toast.error(message || "Failed to save question");
        return;
      }

      setSavedQuestions((prev) => new Set([...prev, questionId]));
      if (message) {
        toast.success(message);
      } else {
        toast.success("Question saved successfully");
      }
    } catch (error) {
      console.error("Error saving question:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to save question. Please try again."
      );
    } finally {
      setSavingQuestionIds((prev) => {
        const next = new Set(prev);
        next.delete(questionId);
        return next;
      });
    }
  };

  const [isFetchingResults, setIsFetchingResults] = useState(false);
  const [resultQuestions, setResultQuestions] = useState([]);
  const [isEvaluatingScore, setIsEvaluatingScore] = useState(false);
  const retryIntervalRef = useRef(null);
  const completeQuizCallbackRef = useRef(null); // true if we need to complete remaining steps after results

  const completeQuizRemainingSteps = async (scorePercentage, status) => {
    if (isMasteryFlowActive()) {
      deactivateMasteryFlow();
      setIsMasteryModeActive(false);
      setShowSidePanel(false);
    }

    if (!embedded && topicId) {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.post(
          `${baseUrl}/api/v2/complete-goal/${topicId}`,
          { score: scorePercentage, status: status },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setLeveledUp(response.data.leveled_up);
        window.goalsData = null;
      } catch (error) {
        console.error(
          "Error completing quiz:",
          error.response?.data || error.message
        );
        // Don't prevent showing results if this fails
      }
    }
  };

  const completeQuiz = async () => {
    try {
      if (!submittedQuestionIndices.current.has(currentQuestionIndex)) {
        try {
          await postAnswerProgress(currentQuestionIndex);
          submittedQuestionIndices.current.add(currentQuestionIndex);
        } catch (error) {
          console.error("Error submitting final answer:", error);
          toast.error(
            error instanceof Error
              ? error.message
              : "Failed to submit your answer. Please try again."
          );
          setSubmitAnswer(false);
          return; // Stay on current question, don't proceed to results
        }
      }

      // Clear any previous result data and immediately switch to the results UI
      setResultQuestions([]);
      if (!quizCompleted) {
        setQuizCompleted(true);
        setShowExplanation(false);
        setSelectedOption(null);
      }

      try {
        // Store flag that we need to complete remaining steps after results are fetched
        completeQuizCallbackRef.current = true;

        await fetchAndApplyQuizResults();

        // If evaluation is still in progress, the retry mechanism will handle
        // calling completeQuizRemainingSteps when evaluation completes
        if (isEvaluatingScore) {
          return; // Exit early, retry will handle completion via callback
        }
      } catch (error) {
        console.error("Error fetching quiz results:", error);
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to fetch quiz results. Please try again."
        );
        setSubmitAnswer(false);
        completeQuizCallbackRef.current = null;
        setQuizCompleted(false);
        return; // Stay on current question, don't proceed to results
      }

      // Complete the remaining steps if evaluation is done
      // Calculate values for completion
      const totalQuestions =
        resultQuestions.length || questionCount || questionList.length;
      const totalCorrect = resultQuestions.reduce((count, question) => {
        if (question?.is_correct) {
          return count + 1;
        }
        return count;
      }, 0);
      const calculatedScorePercentage = totalQuestions
        ? Math.round((totalCorrect / totalQuestions) * 100)
        : 0;
      let calculatedStatus = "";
      if (calculatedScorePercentage === 100)
        calculatedStatus = "Successfully achieved";
      else if (calculatedScorePercentage >= 50)
        calculatedStatus = "Partially achieved";
      else calculatedStatus = "Not achieved";

      await completeQuizRemainingSteps(
        calculatedScorePercentage,
        calculatedStatus
      );
      completeQuizCallbackRef.current = null;
    } catch (error) {
      console.error("Error completing quiz:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again."
      );
      setSubmitAnswer(false);
      return; // Stay on current question, don't proceed to results
    } finally {
      setSubmitAnswer(false);
    }
  };

  const fetchAndApplyQuizResults = async (retryCount = 0) => {
    setIsFetchingResults(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token is missing.");
      }

      const attemptId = quizMetadata?.attempt_id;
      const baseResultUrl = attemptId
        ? `${baseUrl}/v2/quiz/get-result/${attemptId}`
        : `${baseUrl}/v2/quiz/get-result`;
      const resultUrl =
        retryCount > 0 ? `${baseResultUrl}?retry=${retryCount}` : baseResultUrl;

      const response = await fetch(resultUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText
            ? `Failed to fetch quiz results: ${errorText}`
            : `Failed to fetch quiz results (status ${response.status}).`
        );
      }

      const parsedData = await response.json();
      const normalizedResult =
        parsedData?.result && typeof parsedData.result === "object"
          ? parsedData.result
          : parsedData;

      // Check if evaluation is still in progress
      const rawResultStatus = normalizedResult?.status;
      const normalizedResultStatus =
        typeof rawResultStatus === "string"
          ? rawResultStatus.trim().toUpperCase()
          : "";
      const shouldRetryForStatus =
        normalizedResultStatus === "NOT_EVALUATED" ||
        normalizedResultStatus === "ERROR";
      if (shouldRetryForStatus) {
        setIsEvaluatingScore(true);
        setIsFetchingResults(false);

        // Clear any existing retry interval
        if (retryIntervalRef.current) {
          clearTimeout(retryIntervalRef.current);
        }

        // Retry after 10 seconds with incremented retry count
        retryIntervalRef.current = setTimeout(() => {
          fetchAndApplyQuizResults(retryCount + 1);
        }, 10000);

        return; // Don't process results yet
      }

      // Evaluation is complete, stop retrying
      setIsEvaluatingScore(false);
      if (retryIntervalRef.current) {
        clearTimeout(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }

      // Only proceed if status is "Completed"
      if (normalizedResultStatus !== "COMPLETED") {
        throw new Error(`Unexpected result status: ${rawResultStatus}`);
      }

      const normalizedQuestions = Array.isArray(normalizedResult?.questions)
        ? normalizedResult.questions.map((question) => {
            const normalizedEvaluation = normalizeDescriptiveEvaluation(
              question?.descriptiveEvaluation ?? question?.evaluation
            );
            return {
              ...question,
              _normalizedOptions: extractOptions(question),
              ...(normalizedEvaluation
                ? { descriptiveEvaluation: normalizedEvaluation }
                : {}),
            };
          })
        : [];

      const totalQuestions =
        normalizedResult?.total_questions ||
        normalizedQuestions.length ||
        questionCount ||
        questionList.length;

      const totalCorrect = normalizedQuestions.reduce((count, question) => {
        if (question?.is_correct) {
          return count + 1;
        }
        return count;
      }, 0);

      const scorePercentage = totalQuestions
        ? Math.round((totalCorrect / totalQuestions) * 100)
        : 0;

      let status = "";
      if (scorePercentage === 100) status = "Successfully achieved";
      else if (scorePercentage >= 50) status = "Partially achieved";
      else status = "Not achieved";

      setResultQuestions(normalizedQuestions);
      setGoalStatus(status);
      setQuizCompleted(true);
      setScore(totalCorrect);
      setShowExplanation(false);
      setSelectedOption(null);

      // Call the completion callback if it exists (from completeQuiz flow)
      // This happens when retry completes after initial NOT_EVALUATED status
      if (completeQuizCallbackRef.current) {
        try {
          await completeQuizRemainingSteps(scorePercentage, status);
          completeQuizCallbackRef.current = null;
        } catch (error) {
          console.error("Error in completion callback:", error);
          // Don't throw, just log the error
        }
      }
    } catch (error) {
      console.error("Failed to fetch quiz results:", error);
      setIsEvaluatingScore(false);
      if (retryIntervalRef.current) {
        clearTimeout(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
      // Clear callback on error
      completeQuizCallbackRef.current = null;
      // Re-throw error so completeQuiz can handle it and prevent redirect
      throw error;
    } finally {
      setIsFetchingResults(false);
    }
  };

  const handleReturnToGoals = (overrides) => {
    if (overrides && typeof overrides.preventDefault === "function") {
      overrides.preventDefault();
    }

    const statusOverride =
      overrides && typeof overrides === "object"
        ? overrides.statusOverride
        : undefined;
    const scoreOverride =
      overrides && typeof overrides === "object"
        ? overrides.scoreOverride
        : undefined;

    const totalQuestionsForScore =
      questionCount || questionList.length || userAnswers.length;
    const computedScorePercentage = totalQuestionsForScore
      ? Math.round((score / totalQuestionsForScore) * 100)
      : 0;

    const resolvedStatus = statusOverride ?? goalStatus ?? "";
    const resolvedScore =
      typeof scoreOverride === "number"
        ? scoreOverride
        : computedScorePercentage;

    clearQuizStateFromWindow();
    navigate("/", {
      state: {
        completedGoalId: topicId,
        goalStatus: resolvedStatus,
        score: resolvedScore,
      },
    });
  };

  // Close question palette when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        questionPaletteRef.current &&
        !questionPaletteRef.current.contains(event.target)
      ) {
        setShowQuestionPalette(false);
      }
    };

    // Only add listener if palette is open (hover or click)
    if (showQuestionPalette) {
      // Use a small delay to avoid closing immediately on hover
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 200);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showQuestionPalette]);

  // Close upload dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        uploadDropdownRef.current &&
        !uploadDropdownRef.current.contains(event.target)
      ) {
        setShowUploadDropdown(false);
      }
    };

    if (showUploadDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showUploadDropdown]);

  // Cleanup retry interval and callback on unmount
  useEffect(() => {
    return () => {
      if (retryIntervalRef.current) {
        clearTimeout(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
      completeQuizCallbackRef.current = null;
    };
  }, []);

  // Get question status for palette
  const getQuestionStatus = (index) => {
    const question = questionList[index];
    const hasDescriptiveAnswer = isDescriptiveQuestionType(question)
      ? !!descriptiveAnswers[index]?.trim()
      : false;
    if (
      (isDescriptiveQuestionType(question) && hasDescriptiveAnswer) ||
      (!isDescriptiveQuestionType(question) &&
        typeof userAnswers[index] === "number")
    ) {
      return "answered";
    }
    // Check if question has been visited
    const hasBeenVisited =
      index === currentQuestionIndex || visitedQuestions.has(index);

    if (hasBeenVisited) {
      return "not-answered";
    }
    return "not-visited";
  };

  const handleQuestionPaletteClick = (index) => {
    if (index >= 0 && index < questionList.length) {
      // Mark clicked question as visited
      setVisitedQuestions((prev) => new Set([...prev, index]));

      const targetQuestion = questionList[index];
      const nextSelected =
        !isDescriptiveQuestionType(targetQuestion) &&
        typeof userAnswers[index] === "number"
          ? userAnswers[index]
          : null;
      setShowExplanation(false);
      setSelectedOption(nextSelected);
      setCurrentQuestionIndex(index);
      setShowQuestionPalette(false);
      if (window.MathJax?.typesetPromise) {
        window.MathJax.typesetPromise();
      }
    }
  };

  // ================== Need Help behavior ==================
  // ORIGINAL: navigate to /tutor (COMMENTED OUT as requested)
  /*
  const handleNeedHelp = () => {
    const currentQuestionData = questionList[currentQuestionIndex] || {};
    let questionWithOptions = "";
    const helpQuestionText =
      currentQuestionData?.question_text ?? currentQuestionData?.questionText;
    const helpOptions =
      currentQuestionData?._normalizedOptions ??
      extractOptions(currentQuestionData);

    if (helpQuestionText) {
      questionWithOptions = `${helpQuestionText}\n\nOptions:\n`;
      helpOptions.forEach((option, index) => {
        questionWithOptions += `${String.fromCharCode(65 + index)}. ${option}\n`;
      });
      questionWithOptions += "\nPlease help me understand this step by step.";
    }

    navigate("/tutor", {
      state: {
        prefilledText: questionWithOptions,
        activeMode: "stepbystep",
        fromQuestions: true,
        topicId,
      },
    });
  };
  */

  const buildHelpPanelPrefill = (questionData = {}) => {
    const helpQuestionText = normalizeTextValue(
      questionData?.question_text ??
        questionData?.questionText ??
        questionData?.question ??
        questionData?.prompt ??
        ""
    );
    if (!helpQuestionText) {
      return "";
    }
    let formatted = `${helpQuestionText}\n\nOptions:\n`;
    const helpOptions =
      questionData?._normalizedOptions ?? extractOptions(questionData);
    helpOptions.forEach((option, index) => {
      formatted += `${String.fromCharCode(65 + index)}. ${option}\n`;
    });
    formatted += "\nPlease help me understand this step by step.";
    return formatted;
  };

  const scrollToHelpPanel = () => {
    if (typeof window === "undefined") {
      return;
    }
    setTimeout(
      () =>
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        }),
      0
    );
  };

  // NEW: open the side panel instead
  const openHelpPanel = (questionOverride = null) => {
    if (isMasteryModeActive) {
      return;
    }
    console.log("Opening help panel...");
    const questionData =
      questionOverride || questionList[currentQuestionIndex] || {};
    const questionWithOptions = buildHelpPanelPrefill(questionData);

    setQuestionWithOptions(questionWithOptions);
    setShowSidePanel(true);
    scrollToHelpPanel();

    if (questionOverride) {
      setQuestionWithOptions(questionWithOptions);
      scrollToHelpPanel();
      return;
    }
  };

  // ========================================================

  // ===== Build LEFT content (error, completed, or active question) =====
  const normalizedQuestionIndex =
    typeof currentQuestionIndex === "number" && currentQuestionIndex >= 0
      ? currentQuestionIndex
      : 0;
  const currentQuestionData =
    normalizedQuestionIndex < questionList.length
      ? questionList[normalizedQuestionIndex]
      : null;
  const questionIsReady = !!currentQuestionData;
  const currentQuestionIsDescriptive =
    questionIsReady && isDescriptiveQuestionType(currentQuestionData);
  const isCurrentQuestionSubmitted =
    questionIsReady &&
    submittedQuestionIndices.current.has(normalizedQuestionIndex);
  const questionText = questionIsReady
    ? normalizeTextValue(
        currentQuestionData?.question_text ??
          currentQuestionData?.questionText ??
          currentQuestionData?.question ??
          currentQuestionData?.prompt ??
          ""
      )
    : "";
  const options = questionIsReady
    ? currentQuestionData?._normalizedOptions ??
      extractOptions(currentQuestionData)
    : [];
  const descriptiveResponseText =
    questionIsReady &&
    currentQuestionIsDescriptive &&
    descriptiveTextareaRef.current
      ? descriptiveTextareaRef.current.value?.trim() || ""
      : "";
  const descriptiveConfig = currentQuestionIsDescriptive
    ? getDescriptiveConfig(currentQuestionData)
    : null;
  const descriptiveWordLimit =
    currentQuestionIsDescriptive && descriptiveConfig
      ? descriptiveConfig.maxWords
      : null;
  const descriptiveWordCount = currentQuestionIsDescriptive
    ? countWords(descriptiveResponseText)
    : 0;
  const descriptiveWordLimitExceeded =
    currentQuestionIsDescriptive &&
    typeof descriptiveWordLimit === "number" &&
    descriptiveWordLimit > 0 &&
    descriptiveWordCount > descriptiveWordLimit;
  const descriptiveAnswerLabel =
    descriptiveConfig?.label ?? DEFAULT_DESCRIPTIVE_CONFIG.label;
  const descriptiveSupportTextId = `descriptive-support-${normalizedQuestionIndex}`;
  const descriptiveTextareaStyle = currentQuestionIsDescriptive
    ? {
        minHeight: `${
          descriptiveConfig?.minHeightPx ??
          DEFAULT_DESCRIPTIVE_CONFIG.minHeightPx
        }px`,
        maxHeight: `${Math.round(
          (descriptiveConfig?.maxViewportHeightRatio ??
            DEFAULT_DESCRIPTIVE_CONFIG.maxViewportHeightRatio) * 100
        )}vh`,
        overflowY: "auto",
      }
    : undefined;
  const hasDescriptiveImages =
    currentQuestionIsDescriptive &&
    Array.isArray(descriptiveImages[normalizedQuestionIndex]) &&
    descriptiveImages[normalizedQuestionIndex].length > 0;
  const hasDescriptiveResponse =
    currentQuestionIsDescriptive &&
    ((descriptiveTextareaRef.current?.value?.trim().length > 0 &&
      !descriptiveWordLimitExceeded) ||
      hasDescriptiveImages);
  const storedSelectedOption =
    typeof userAnswers[normalizedQuestionIndex] === "number"
      ? userAnswers[normalizedQuestionIndex]
      : null;
  const effectiveSelectedOption =
    typeof selectedOption === "number" ? selectedOption : storedSelectedOption;
  const hasObjectiveSelection =
    questionIsReady &&
    !currentQuestionIsDescriptive &&
    typeof effectiveSelectedOption === "number";
  const requiresAnswerInput =
    questionIsReady &&
    (currentQuestionIsDescriptive || (options?.length ?? 0) > 0);
  const hasAnsweredCurrentQuestion = !questionIsReady
    ? false
    : !requiresAnswerInput
    ? true
    : currentQuestionIsDescriptive
    ? hasDescriptiveResponse
    : hasObjectiveSelection;
  const isNextDisabled = submittingAnswer || !hasAnsweredCurrentQuestion;
  const correctAnswerIndex = questionIsReady
    ? resolveCorrectIndex(currentQuestionData)
    : null;
  const descriptiveCorrectText =
    questionIsReady && currentQuestionIsDescriptive
      ? normalizeTextValue(
          currentQuestionData?.correct_option_text ??
            currentQuestionData?.correct_answer ??
            currentQuestionData?.expected_answer ??
            ""
        )
      : "";
  const explanationText = questionIsReady
    ? convertBreakTagsToNewlines(
        normalizeTextValue(
          currentQuestionData?.check_answer_text ??
            currentQuestionData?.resoning ??
            currentQuestionData?.reasoning ??
            currentQuestionData?.explanation ??
            ""
        )
      )
    : "";
  const descriptiveEvaluation = currentQuestionIsDescriptive
    ? currentQuestionData?.descriptiveEvaluation ?? null
    : null;
  const descriptiveStepBreakdown = Array.isArray(
    descriptiveEvaluation?.stepBreakdown
  )
    ? descriptiveEvaluation.stepBreakdown
    : [];
  const descriptiveScore = Number.isFinite(descriptiveEvaluation?.score)
    ? descriptiveEvaluation.score
    : null;
  const descriptiveTotalMarks = Number.isFinite(
    descriptiveEvaluation?.totalMarks
  )
    ? descriptiveEvaluation.totalMarks
    : null;
  const descriptiveScoreDisplay = formatMarks(descriptiveScore);
  const descriptiveTotalDisplay = formatMarks(descriptiveTotalMarks);
  const descriptiveFeedbackText =
    currentQuestionIsDescriptive &&
    (descriptiveEvaluation?.feedback || explanationText)
      ? convertBreakTagsToNewlines(
          descriptiveEvaluation?.feedback || explanationText
        )
      : "";
  const currentQuestionId = questionIsReady
    ? resolveQuestionId(
        currentQuestionData,
        `question-${normalizedQuestionIndex + 1}`
      )
    : null;
  const isCurrentQuestionFlagged = Boolean(
    currentQuestionId && flaggedQuestions.has(currentQuestionId)
  );
  const isCurrentQuestionSaved = Boolean(
    currentQuestionId && savedQuestions.has(currentQuestionId)
  );
  const isSavingCurrentQuestion = Boolean(
    currentQuestionId && savingQuestionIds.has(currentQuestionId)
  );
  const isAnswerCorrect = questionIsReady
    ? currentQuestionIsDescriptive
      ? typeof currentQuestionData?.is_correct === "boolean"
        ? currentQuestionData.is_correct
        : null
      : typeof correctAnswerIndex === "number" &&
        selectedOption === correctAnswerIndex
    : null;
  const displayedCorrectOption = currentQuestionIsDescriptive
    ? descriptiveCorrectText
    : typeof correctAnswerIndex === "number" &&
      correctAnswerIndex >= 0 &&
      correctAnswerIndex < options.length
    ? options[correctAnswerIndex]
    : "";
  const totalQuestionsForDisplay =
    resultQuestions.length || questionCount || questionList.length;
  const answeredCount = questionList.reduce((count, question, index) => {
    if (isDescriptiveQuestionType(question)) {
      return count;
    }
    return count + (typeof userAnswers[index] === "number" ? 1 : 0);
  }, 0);
  const savedCount = savedQuestions.size;
  const flaggedCount = flaggedQuestions.size;
  const notAttemptedCount = Math.max(
    (totalQuestionsForDisplay || 0) - answeredCount,
    0
  );

  // Calculate correct and incorrect counts from resultQuestions
  const correctCount =
    resultQuestions.length > 0
      ? resultQuestions.reduce((count, question) => {
          if (question?.is_correct === true) {
            return count + 1;
          }
          return count;
        }, 0)
      : 0;
  const incorrectCount =
    resultQuestions.length > 0
      ? resultQuestions.reduce((count, question) => {
          const notAttempted =
            typeof question?.user_answer === "undefined" &&
            typeof question?.option_index !== "number";
          if (!notAttempted && question?.is_correct === false) {
            return count + 1;
          }
          return count;
        }, 0)
      : 0;
  const displayQuestionNumber =
    (questionIsReady &&
      (currentQuestionData?.id ??
        currentQuestionData?.question_number ??
        currentQuestionData?.qn)) ||
    currentQuestionIndex + 1;
  const isLastQuestion = questionCount
    ? currentQuestionIndex + 1 >= questionCount
    : currentQuestionIndex >= questionList.length - 1;
  // Button label: always show "Next Question" while timer is running,
  // and only show "Finish Quiz" once the timer has expired.
  const nextButtonLabel = remainingTime === 0 ? "Finish Quiz" : "Next Question";
  const canSkipToNextQuestion = currentQuestionIndex < questionList.length - 1;
  const checkAnswerButtonText =
    currentQuestionData?.check_answer_button_text ?? "Evaluate Answer";
  const isMathJaxLoading = mathJaxStatus === "loading";
  const didMathJaxError = mathJaxStatus === "error";
  // We only treat a descriptive question as "evaluated" when the backend has
  // actually returned a structured descriptiveEvaluation object for it.
  // (Static fields like correct answers or explanations may exist even before
  // evaluation and should NOT by themselves enable the Check Answer button.)
  const hasDescriptiveEvaluation =
    currentQuestionIsDescriptive &&
    !!currentQuestionData?.descriptiveEvaluation;
  const currentKeyHint = questionIsReady
    ? normalizeTextValue(
        currentQuestionData?.resoning ??
          currentQuestionData?.reasoning ??
          currentQuestionData?.explanation ??
          ""
      )
    : "";
  const canCheckAnswer =
    !isMasteryModeActive &&
    !showExplanation &&
    (currentQuestionIsDescriptive
      ? hasDescriptiveEvaluation || hasDescriptiveResponse
      : typeof selectedOption === "number");

  useEffect(() => {
    if (!currentQuestionIsDescriptive) return;
    const textarea = descriptiveTextareaRef.current;
    if (!textarea) return;
    const config = descriptiveConfig ?? DEFAULT_DESCRIPTIVE_CONFIG;
    const viewportHeight =
      typeof window !== "undefined" ? window.innerHeight : null;
    const maxHeightPx =
      viewportHeight && config.maxViewportHeightRatio
        ? Math.max(
            config.minHeightPx,
            Math.round(viewportHeight * config.maxViewportHeightRatio)
          )
        : config.minHeightPx;
    textarea.style.height = "auto";
    const desiredHeight = Math.min(
      Math.max(textarea.scrollHeight, config.minHeightPx),
      maxHeightPx
    );
    textarea.style.height = `${desiredHeight}px`;
  }, [
    currentQuestionIsDescriptive,
    descriptiveResponseText,
    currentQuestionIndex,
    descriptiveConfig,
  ]);

  // Rotate descriptive status messages while descriptive answer is being submitted
  useEffect(() => {
    const shouldRun =
      submittingAnswer &&
      currentQuestionIsDescriptive &&
      DESCRIPTIVE_STATUS_MESSAGES.length > 1;
    if (!shouldRun) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      setDescriptiveStatusIndex(
        (prev) => (prev + 1) % DESCRIPTIVE_STATUS_MESSAGES.length
      );
    }, 2000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [submittingAnswer, currentQuestionIsDescriptive]);

  const showError =
    !embedded && (!questionList.length || storedTopicId !== topicId);

  const LeftPanelContent = (
    <div
      className={`${
        embedded ? "bg-white h-full" : "bg-white min-h-screen"
      } p-6`}
    >
      <div
        className={`p-5 rounded-3xl max-w-full mx-auto ${
          embedded ? "h-full" : "h-[90vh]"
        } overflow-y-auto`}
      >
        {showError ? (
          <div className="flex justify-center items-center h-full bg-blue-50">
            <div className="bg-white p-8 rounded-xl shadow-md max-w-md">
              <h2 className="text-xl font-bold text-red-600 mb-4">Error</h2>
              <p className="text-gray-700">
                No active quiz found. Please start a goal.
              </p>
              <button
                onClick={() => navigate("/")}
                className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700"
              >
                Return to My Goals
              </button>
            </div>
          </div>
        ) : quizCompleted ? (
          (() => {
            const resultsHeader = (
              <div className="flex items-start justify-between relative">
                <h1 className="text-2xl font-bold">Quiz Completed!</h1>
                <div className="fixed top-6 right-6 z-50">
                  <button
                    type="button"
                    aria-label="Close"
                    className="text-gray-600 hover:text-red-600 transition-colors"
                    onClick={() => {
                      if (showSidePanel) {
                        setShowSidePanel(false);
                        return;
                      }
                      if (embedded && onClose) return onClose();
                      navigate("/");
                    }}
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>
            );
            const shouldShowInitialResultsLoader =
              isFetchingResults && !resultQuestions.length;
            if (shouldShowInitialResultsLoader) {
              return (
                <>
                  {resultsHeader}
                  <div className="flex flex-col justify-center items-center py-10">
                    <SimpleThreeDotsLoader message="Preparing your results..." />
                    <p className="mt-3 text-sm text-gray-600 text-center">
                      Hang tight while we fetch your performance summary.
                    </p>
                  </div>
                </>
              );
            }
            if (isEvaluatingScore) {
              return (
                <>
                  {resultsHeader}
                  <div className="flex flex-col justify-center items-center py-10">
                    <SimpleThreeDotsLoader message="Evaluating your answers..." />
                    <p className="mt-3 text-sm text-gray-600 text-center">
                      Hang tight while we complete the scoring and finalize your
                      report.
                    </p>
                  </div>
                </>
              );
            }
            const totalQuestions =
              resultQuestions.length || questionCount || questionList.length;
            const finalScore = totalQuestions
              ? Math.round((score / totalQuestions) * 100)
              : 0;
            const marksObtained = score;
            const totalMarks = totalQuestions;
            return (
              <>
                {resultsHeader}
                <div className="text-center mb-8">
                  <div className="text-5xl font-bold text-blue-600 mb-2">
                    {finalScore}%
                  </div>
                  <p className="text-gray-600">
                    You got {score} out of {totalQuestions} questions correct
                  </p>
                  {/* <p className="text-gray-700 mt-1">
                    Marks: {marksObtained} / {totalMarks}
                  </p> */}
                  {/* <div
                    className={`mt-4 w-1/2 mx-auto p-3 rounded-lg text-white font-medium ${
                      finalScore > 80
                        ? "bg-green-600"
                        : finalScore >= 50
                        ? "bg-yellow-500"
                        : finalScore >= 10
                        ? "bg-blue-500"
                        : "bg-red-500"
                    }`}
                  >
                    Goal Status: {goalStatus}
                  </div> */}
                </div>
                {leveledUp && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md mb-6">
                    <p className="font-bold">Congratulations!</p>
                    <p>
                      You've leveled up in {goalDetails.subject}! Your new level
                      is {goalDetails.current_level + 1}.
                    </p>
                  </div>
                )}
                <div className="mt-8 bg-gray-50 border border-gray-200 rounded-2xl p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Question Summary
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-green-100 text-green-700">
                      <span className="font-medium">Correct</span>
                      <span className="text-xl font-bold">{correctCount}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-red-100 text-red-600">
                      <span className="font-medium">Incorrect</span>
                      <span className="text-xl font-bold">
                        {incorrectCount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-orange-100 text-orange-600">
                      <span className="font-medium">Not Attempted</span>
                      <span className="text-xl font-bold">0</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-yellow-100 text-yellow-700">
                      <span className="font-medium">Saved for Revision</span>
                      <span className="text-xl font-bold">{savedCount}</span>
                    </div>
                  </div>
                </div>
                {isEvaluatingScore ? (
                  <div className="flex flex-col justify-center items-center py-10">
                    <div className="w-full max-w-md mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Evaluating your score...
                        </span>
                        <span className="text-xs text-gray-500">
                          Please wait
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden relative">
                        <div
                          className="h-full bg-blue-600 rounded-full"
                          style={{
                            width: "100%",
                            background:
                              "linear-gradient(90deg, #2563eb 0%, #3b82f6 50%, #2563eb 100%)",
                            backgroundSize: "200% 100%",
                            animation: "shimmer 2s infinite linear",
                          }}
                        ></div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      This may take a few moments
                    </p>
                    <style>{`
                      @keyframes shimmer {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                      }
                    `}</style>
                  </div>
                ) : isFetchingResults ? (
                  <div className="flex justify-center items-center py-10">
                    <SimpleThreeDotsLoader message="Fetching detailed results..." />
                  </div>
                ) : (resultQuestions.length > 0
                    ? resultQuestions
                    : questionList
                  ).length > 0 ? (
                  <MathJaxContext config={mathJaxConfig}>
                    <div className="mt-8">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        Review Your Answers
                      </h3>
                      <div className="space-y-4">
                        {(resultQuestions.length > 0
                          ? resultQuestions
                          : questionList
                        ).map((question, index) => {
                          const questionId = resolveQuestionId(
                            question,
                            `question-${index + 1}`
                          );
                          const isDescriptiveReviewQuestion =
                            isDescriptiveQuestionType(question);
                          const descriptiveEvaluationForReview =
                            isDescriptiveReviewQuestion &&
                            question?.descriptiveEvaluation
                              ? question.descriptiveEvaluation
                              : isDescriptiveReviewQuestion
                              ? normalizeDescriptiveEvaluation(
                                  question?.evaluation
                                )
                              : null;
                          const descriptiveStepBreakdown = Array.isArray(
                            descriptiveEvaluationForReview?.stepBreakdown
                          )
                            ? descriptiveEvaluationForReview.stepBreakdown
                            : [];
                          const descriptiveScoreValue =
                            isDescriptiveReviewQuestion
                              ? getFiniteNumber(question?.score) ??
                                getFiniteNumber(
                                  descriptiveEvaluationForReview?.score
                                )
                              : null;
                          const descriptiveTotalMarksValue =
                            isDescriptiveReviewQuestion
                              ? getFiniteNumber(question?.totalMarks) ??
                                getFiniteNumber(
                                  descriptiveEvaluationForReview?.totalMarks
                                )
                              : null;
                          const descriptiveFeedbackText =
                            isDescriptiveReviewQuestion
                              ? convertBreakTagsToNewlines(
                                  normalizeTextValue(
                                    descriptiveEvaluationForReview?.feedback ??
                                      question?.feedback ??
                                      ""
                                  )
                                )
                              : "";
                          const questionPrompt = normalizeTextValue(
                            question?.question_text ??
                              question?.questionText ??
                              question?.question ??
                              question?.prompt ??
                              ""
                          );
                          const optionsForQuestion =
                            question?._normalizedOptions ??
                            extractOptions(question);
                          const userSelectionIndex =
                            typeof question?.option_index === "number"
                              ? question.option_index
                              : typeof userAnswers[index] === "number"
                              ? userAnswers[index]
                              : null;
                          const correctIndex =
                            typeof question?.correct_option_index === "number"
                              ? question.correct_option_index
                              : resolveCorrectIndex(question);
                          const userSelectionText = question?.user_answer
                            ? normalizeTextValue(question.user_answer)
                            : userSelectionIndex !== null &&
                              userSelectionIndex >= 0 &&
                              userSelectionIndex < optionsForQuestion.length
                            ? optionsForQuestion[userSelectionIndex]
                            : "";
                          const correctOptionText = question?.correct_answer
                            ? normalizeTextValue(question.correct_answer)
                            : typeof correctIndex === "number" &&
                              correctIndex >= 0 &&
                              correctIndex < optionsForQuestion.length
                            ? optionsForQuestion[correctIndex]
                            : "";
                          const explanation = convertBreakTagsToNewlines(
                            normalizeTextValue(
                              question?.check_answer_text ??
                                question?.resoning ??
                                question?.reasoning ??
                                question?.explanation ??
                                question?.explanation_text ??
                                ""
                            )
                          );
                          const notAttempted =
                            typeof question?.user_answer === "undefined" &&
                            typeof userSelectionIndex !== "number";
                          const isCorrect =
                            typeof question?.is_correct === "boolean"
                              ? question.is_correct
                              : !notAttempted &&
                                typeof correctIndex === "number" &&
                                userSelectionIndex === correctIndex;
                          const containerClasses = isCorrect
                            ? "bg-green-50 border border-green-200"
                            : "bg-red-50 border border-red-200";
                          const statusLabel = notAttempted
                            ? "Not answered"
                            : isCorrect
                            ? "✓ Correct!"
                            : "✗ Incorrect.";

                          // Marks handling (supports MCQ and descriptive):
                          // Prefer explicit marks metadata from backend if available.
                          const rawMaxMarks =
                            question?.marks ??
                            question?.max_marks ??
                            question?.total_marks ??
                            question?.score_per_question ??
                            1;
                          const baseMaxMarks = Number.isFinite(
                            Number(rawMaxMarks)
                          )
                            ? Number(rawMaxMarks)
                            : 1;

                          const rawObtainedMarks =
                            question?.obtained_marks ?? question?.score;
                          const baseObtainedMarks = Number.isFinite(
                            Number(rawObtainedMarks)
                          )
                            ? Number(rawObtainedMarks)
                            : isCorrect
                            ? baseMaxMarks
                            : 0;

                          const displayedMarksValue =
                            isDescriptiveReviewQuestion &&
                            descriptiveScoreValue !== null
                              ? descriptiveScoreValue
                              : baseObtainedMarks;
                          const displayedMaxMarksValue =
                            isDescriptiveReviewQuestion &&
                            descriptiveTotalMarksValue !== null
                              ? descriptiveTotalMarksValue
                              : baseMaxMarks;
                          const displayedMarks =
                            formatMarks(displayedMarksValue) ??
                            (typeof displayedMarksValue === "number" &&
                            Number.isFinite(displayedMarksValue)
                              ? displayedMarksValue.toString()
                              : "0");
                          const displayedMaxMarks =
                            formatMarks(displayedMaxMarksValue) ??
                            (typeof displayedMaxMarksValue === "number" &&
                            Number.isFinite(displayedMaxMarksValue)
                              ? displayedMaxMarksValue.toString()
                              : "0");

                          const isSaved = savedQuestions.has(questionId);
                          const isSavingThisQuestion =
                            savingQuestionIds.has(questionId);
                          const handleSaveFromReview = () =>
                            handleSaveQuestion(question, index);
                          const shouldShowAskAanya = !isCorrect;
                          const reviewItemKey = questionId
                            ? `${questionId}-${index}`
                            : `question-review-${index}`;

                          return (
                            <div
                              key={reviewItemKey}
                              className={`rounded-2xl p-4 ${containerClasses}`}
                            >
                              <div className="flex flex-wrap gap-4 justify-between">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white text-gray-800 flex items-center justify-center font-semibold border border-gray-200">
                                    {index + 1}
                                  </div>
                                  <div className="flex-1">
                                    {questionPrompt && (
                                      <MathJaxRenderer className="text-base font-semibold mb-2 whitespace-pre-line leading-relaxed">
                                        {questionPrompt}
                                      </MathJaxRenderer>
                                    )}
                                    <div className="font-semibold mb-1 text-sm flex items-center gap-2">
                                      <span>{statusLabel}</span>
                                      <span className="text-xs text-gray-600">
                                        Marks: {displayedMarks} /{" "}
                                        {displayedMaxMarks}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-800 space-y-2">
                                      <div className="mt-2">
                                        <span className="font-semibold">
                                          Your answer:
                                        </span>{" "}
                                        {notAttempted ? (
                                          <span className="text-red-500">
                                            Not answered
                                          </span>
                                        ) : (
                                          <MathJaxRenderer className="mt-1 whitespace-pre-line leading-relaxed">
                                            {normalizeTextValue(
                                              userSelectionText
                                            ) || ""}
                                          </MathJaxRenderer>
                                        )}
                                      </div>
                                      {!isDescriptiveReviewQuestion &&
                                        !isCorrect &&
                                        correctOptionText && (
                                          <div className="mt-3">
                                            <span className="font-semibold">
                                              Correct answer:
                                            </span>
                                            <MathJaxRenderer className="mt-1 whitespace-pre-line leading-relaxed">
                                              {correctOptionText}
                                            </MathJaxRenderer>
                                          </div>
                                        )}
                                      {!isDescriptiveReviewQuestion &&
                                        explanation && (
                                          <div className="mt-3">
                                            <span className="font-semibold">
                                              Explanation:
                                            </span>
                                            <MathJaxRenderer className="mt-1 whitespace-pre-line leading-relaxed">
                                              {explanation}
                                            </MathJaxRenderer>
                                          </div>
                                        )}
                                      {isDescriptiveReviewQuestion &&
                                        descriptiveFeedbackText && (
                                          <div className="mt-3">
                                            <span className="font-semibold">
                                              Feedback:
                                            </span>
                                            <MathJaxRenderer className="mt-1 whitespace-pre-line leading-relaxed">
                                              {descriptiveFeedbackText}
                                            </MathJaxRenderer>
                                          </div>
                                        )}
                                      {isDescriptiveReviewQuestion &&
                                        !descriptiveFeedbackText &&
                                        explanation && (
                                          <div className="mt-3">
                                            <span className="font-semibold">
                                              Explanation:
                                            </span>
                                            <MathJaxRenderer className="mt-1 whitespace-pre-line leading-relaxed">
                                              {explanation}
                                            </MathJaxRenderer>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex-shrink-0">
                                  <button
                                    onClick={handleSaveFromReview}
                                    disabled={isSavingThisQuestion || isSaved}
                                    className={`px-3 py-1.5 rounded-full font-medium text-xs flex items-center gap-1.5 transition-all ${
                                      isSaved
                                        ? "bg-green-100 text-green-700 border border-green-300 cursor-default"
                                        : isSavingThisQuestion
                                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                        : "bg-white text-blue-600 border border-blue-300 hover:bg-blue-50 hover:border-blue-400"
                                    }`}
                                  >
                                    {isSaved ? (
                                      <>
                                        <svg
                                          className="w-3.5 h-3.5"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                        <span>Saved</span>
                                      </>
                                    ) : (
                                      <>
                                        <svg
                                          className="w-3.5 h-3.5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                                          />
                                        </svg>
                                        <span>
                                          {isSavingThisQuestion
                                            ? "Saving..."
                                            : ""}
                                        </span>
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                              {isDescriptiveReviewQuestion &&
                                descriptiveStepBreakdown.length > 0 && (
                                  <div className="mt-4">
                                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                                      <div className="px-4 py-3 border-b border-gray-100">
                                        <p
                                          style={{
                                            paddingBottom: "0px",
                                            marginBottom: "0px",
                                          }}
                                          className="text-sm font-semibold text-gray-800"
                                        >
                                          Step-by-step evaluation
                                        </p>
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="bg-gray-50 text-gray-600">
                                              <th className="py-2 px-3 text-left w-20">
                                                Step
                                              </th>
                                              <th className="py-2 px-3 text-left">
                                                Solution
                                              </th>
                                              <th className="py-2 px-3 text-right whitespace-nowrap">
                                                Marks
                                              </th>
                                              <th className="py-2 px-3 text-left">
                                                Justification
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {descriptiveStepBreakdown.map(
                                              (stepRow, stepIndex) => {
                                                const stepLabel =
                                                  stepRow?.step ??
                                                  stepIndex + 1;
                                                const stepMarksValue =
                                                  getFiniteNumber(
                                                    stepRow?.marks
                                                  );
                                                const stepMaxMarksValue =
                                                  getFiniteNumber(
                                                    stepRow?.maxMarks
                                                  );
                                                const stepMarksDisplay =
                                                  formatMarks(stepMarksValue) ??
                                                  (typeof stepMarksValue ===
                                                    "number" &&
                                                  Number.isFinite(
                                                    stepMarksValue
                                                  )
                                                    ? stepMarksValue.toString()
                                                    : "0");
                                                const stepMaxDisplay =
                                                  formatMarks(
                                                    stepMaxMarksValue
                                                  ) ??
                                                  (typeof stepMaxMarksValue ===
                                                    "number" &&
                                                  Number.isFinite(
                                                    stepMaxMarksValue
                                                  )
                                                    ? stepMaxMarksValue.toString()
                                                    : "0");
                                                const solutionText =
                                                  normalizeTextValue(
                                                    stepRow?.solution ||
                                                      stepRow?.feedback ||
                                                      ""
                                                  );
                                                const justificationText =
                                                  normalizeTextValue(
                                                    stepRow?.justification || ""
                                                  );
                                                return (
                                                  <tr
                                                    key={`${questionId}-step-${stepLabel}-${stepIndex}`}
                                                    className="border-b border-gray-100 last:border-b-0"
                                                  >
                                                    <td className="py-2 px-3 font-medium text-gray-700 whitespace-nowrap">
                                                      {stepLabel}
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-700">
                                                      <MathJaxRenderer className="whitespace-pre-line leading-relaxed">
                                                        {solutionText || "-"}
                                                      </MathJaxRenderer>
                                                    </td>
                                                    <td className="py-2 px-3 text-right font-semibold text-gray-800 whitespace-nowrap">
                                                      {stepMarksDisplay}/
                                                      {stepMaxDisplay}
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-700">
                                                      {justificationText ? (
                                                        <MathJaxRenderer className="whitespace-pre-line leading-relaxed">
                                                          {justificationText}
                                                        </MathJaxRenderer>
                                                      ) : (
                                                        <span className="text-gray-400">
                                                          -
                                                        </span>
                                                      )}
                                                    </td>
                                                  </tr>
                                                );
                                              }
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              {shouldShowAskAanya && (
                                <div className="mt-4 flex justify-end">
                                  <button
                                    onClick={() => openHelpPanel(question)}
                                    className="px-4 py-2 rounded-full bg-primary text-white font-semibold hover:bg-primary-dark transition-colors"
                                  >
                                    Ask Aanya
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </MathJaxContext>
                ) : (
                  <p className="text-center text-gray-600 mt-4">
                    No result data is available at this time.
                  </p>
                )}
                {!embedded && (
                  <div className="text-center mt-6">
                    <button
                      onClick={handleReturnToGoals}
                      className="px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 font-medium"
                    >
                      Return to My Goals
                    </button>
                  </div>
                )}
              </>
            );
          })()
        ) : (
          <MathJaxContext config={mathJaxConfig}>
            <div className="mb-6">
              <div className="flex items-start justify-between relative">
                <p className="text-lg text-black">
                  Question {displayQuestionNumber}{" "}
                  {/* of {totalQuestionsForDisplay} */}
                </p>
                <div className="flex flex-col items-end gap-3">
                  {remainingTime !== null && (
                    <div className="text-lg font-bold text-red-600 bg-red-50 px-3 py-1 rounded-lg border border-red-200">
                      {formatTime(remainingTime)}
                    </div>
                  )}
                </div>
                <div className="fixed top-6 right-6 z-50">
                  <button
                    type="button"
                    aria-label="Close"
                    className="text-gray-600 hover:text-red-600 transition-colors"
                    onClick={() => {
                      if (showSidePanel) {
                        setShowSidePanel(false);
                        return;
                      }
                      if (quizCompleted) {
                        if (embedded && onClose) return onClose();
                        navigate("/");
                        return;
                      }
                      // Show confirmation if quiz is not completed
                      setShowCloseConfirmation(true);
                    }}
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 mb-6 w-full text-black">
              {isMathJaxLoading ? (
                <div className="py-10 flex justify-center w-full text-center text-gray-500">
                  <SimpleThreeDotsLoader message="Loading Questions" />
                </div>
              ) : !questionIsReady ? (
                <div className="py-10 flex justify-center w-full text-center text-gray-500">
                  <SimpleThreeDotsLoader message="Loading next question..." />
                </div>
              ) : (
                <>
                  {didMathJaxError && (
                    <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                      Math expressions may appear as plain text while we finish
                      loading them.
                    </div>
                  )}

                  {(questionText ||
                    options.length ||
                    currentQuestionIsDescriptive) && (
                    <>
                      {questionText && (
                        <div className="mb-4">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <MathJaxRenderer className="flex-1 text-lg font-semibold whitespace-pre-line">
                              {questionText || ""}
                            </MathJaxRenderer>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* Mark for Revision Button */}
                              <button
                                onClick={() => handleSaveQuestion()}
                                disabled={
                                  isSavingCurrentQuestion ||
                                  isCurrentQuestionSaved
                                }
                                className={`p-2 rounded-full flex items-center justify-center transition-all ${
                                  isSavingCurrentQuestion
                                    ? "opacity-50 cursor-not-allowed text-blue-400 bg-blue-50"
                                    : isCurrentQuestionSaved
                                    ? "bg-primary-light text-primary border border-primary cursor-default shadow-sm"
                                    : "text-primary hover:text-primary-dark hover:bg-primary-light"
                                }`}
                                title={
                                  isCurrentQuestionSaved
                                    ? "Saved for revision"
                                    : "Save for revision"
                                }
                              >
                                {isCurrentQuestionSaved ? (
                                  <FaBookmark
                                    size={16}
                                    className="text-primary"
                                  />
                                ) : (
                                  <FaRegBookmark size={16} />
                                )}
                              </button>

                              {/* Flag Issue icon in header (icon-only) */}
                              <button
                                onClick={handleOpenFlagModal}
                                className={`flex items-center justify-center p-2 rounded-full transition-all ${
                                  isCurrentQuestionFlagged
                                    ? "text-red-600 hover:bg-red-50"
                                    : "text-primary hover:bg-primary-light"
                                }`}
                                title={
                                  isCurrentQuestionFlagged
                                    ? "Question flagged"
                                    : "Flag Issue"
                                }
                              >
                                {isCurrentQuestionFlagged ? (
                                  <FaFlag size={16} className="text-red-600" />
                                ) : (
                                  <FaRegFlag size={16} />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {currentQuestionIsDescriptive && (
                        <div className="mb-6">
                          {/* <label className="block text-sm font-medium text-gray-700 mb-2">
                            {descriptiveAnswerLabel}
                            {typeof descriptiveWordLimit === "number"
                              ? ` (up to ${descriptiveWordLimit} words)`
                              : ""}
                          </label> */}
                          <div className="relative">
                            <textarea
                              ref={descriptiveTextareaRef}
                              // value={descriptiveResponseText}
                              disabled={isCurrentQuestionSubmitted}
                              // onChange={(e) =>
                              //   handleDescriptiveAnswerChange(
                              //     normalizedQuestionIndex,
                              //     e.target.value
                              //   )
                              // }
                              placeholder="Type your answer here..."
                              aria-describedby={descriptiveSupportTextId}
                              className="w-full border border-gray-200 rounded-2xl px-4 py-3 pb-16 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none leading-relaxed"
                              style={descriptiveTextareaStyle}
                            />
                            <div className="absolute bottom-3 left-4 flex gap-4 text-gray-500">
                              <div
                                className="relative flex items-center gap-2"
                                ref={uploadDropdownRef}
                              >
                                {/* Hidden file input */}
                                <input
                                  ref={descriptiveFileInputRef}
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  disabled={isCurrentQuestionSubmitted}
                                  onChange={(e) => {
                                    handleDescriptiveImagesUpload(
                                      normalizedQuestionIndex,
                                      e.target.files
                                    );
                                    // allow re-uploading the same file(s)
                                    e.target.value = "";
                                  }}
                                  className="hidden"
                                />
                                {/* Icon button */}
                                <FaPlusCircle
                                  size={25}
                                  className={`cursor-pointer ${
                                    isCurrentQuestionSubmitted
                                      ? "opacity-50"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (isCurrentQuestionSubmitted) return;
                                    setShowUploadDropdown(!showUploadDropdown);
                                  }}
                                  title="Add files"
                                />
                                {/* Dropdown menu */}
                                {showUploadDropdown && (
                                  <div className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[180px]">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        descriptiveFileInputRef.current?.click();
                                        setShowUploadDropdown(false);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      Upload File
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setShowCameraModal(true);
                                        setShowUploadDropdown(false);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      Camera
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        toast.info(
                                          "Upload from Phone feature coming soon!"
                                        );
                                        setShowUploadDropdown(false);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      Upload from Phone
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* <div
                            id={descriptiveSupportTextId}
                            className="mt-2 flex flex-wrap items-center gap-3 text-sm"
                          >
                            <span
                              className={`font-medium ${
                                descriptiveWordLimitExceeded
                                  ? "text-red-600"
                                  : "text-gray-600"
                              }`}
                            >
                              Word count: {descriptiveWordCount}
                              {typeof descriptiveWordLimit === "number"
                                ? ` / ${descriptiveWordLimit}`
                                : ""}
                            </span>
                            {descriptiveWordLimitExceeded ? (
                              <span className="text-red-600">
                                Please stay within the limit.
                              </span>
                            ) : (
                              <span className="text-gray-500">
                                Use complete sentences and answer in your own
                                words.
                              </span>
                            )}
                          </div> */}
                          <div className="mt-4">
                            {Array.isArray(
                              descriptiveImages[normalizedQuestionIndex]
                            ) &&
                              descriptiveImages[normalizedQuestionIndex]
                                .length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-gray-600 mb-2">
                                    Uploaded files
                                  </p>
                                  <div className="flex flex-wrap gap-3">
                                    {descriptiveImages[
                                      normalizedQuestionIndex
                                    ].map((img, imgIndex) => (
                                      <button
                                        key={img.id}
                                        type="button"
                                        onClick={() =>
                                          openAttachmentPreview(
                                            descriptiveImages[
                                              normalizedQuestionIndex
                                            ],
                                            imgIndex
                                          )
                                        }
                                        className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 group"
                                      >
                                        {img.isPdf ? (
                                          <div className="flex flex-col items-center justify-center w-full h-full bg-red-50 text-red-600">
                                            <svg
                                              className="w-6 h-6"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M6 2a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8.828a2 2 0 00-.586-1.414l-4.828-4.828A2 2 0 0013.172 2H6z"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                              />
                                              <path
                                                d="M13 2v5a1 1 0 001 1h5"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                              />
                                              <text
                                                x="12"
                                                y="17"
                                                textAnchor="middle"
                                                fontSize="7"
                                                fontWeight="600"
                                                fill="currentColor"
                                              >
                                                PDF
                                              </text>
                                            </svg>
                                          </div>
                                        ) : (
                                          <img
                                            src={img.url}
                                            alt={img.name || "Uploaded file"}
                                            className="w-full h-full object-cover"
                                          />
                                        )}
                                        <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <div
                                          onClick={(e) => {
                                            if (isCurrentQuestionSubmitted)
                                              return;
                                            e.stopPropagation();
                                            openImageDeleteConfirm(
                                              normalizedQuestionIndex,
                                              img.id
                                            );
                                          }}
                                          className={`absolute top-1 right-1 bg-white/90 rounded-full p-1 text-gray-700 shadow ${
                                            isCurrentQuestionSubmitted
                                              ? "opacity-40 cursor-not-allowed"
                                              : "hover:bg-red-600 hover:text-white cursor-pointer"
                                          }`}
                                          aria-label="Remove file"
                                          role="button"
                                        >
                                          <FaTimes className="w-3 h-3" />
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                      {!currentQuestionIsDescriptive && (
                        <div className="space-y-3 mb-6">
                          {options.map((option, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                if (isCurrentQuestionSubmitted) return;
                                handleOptionSelect(index);
                              }}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedOption === index
                                  ? isCurrentQuestionSubmitted
                                    ? "border-green-500 bg-green-50 cursor-default"
                                    : "border-[#0D99FF] bg-blue-50"
                                  : isCurrentQuestionSubmitted
                                  ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                                  : "border-gray-200 hover:border-primary-light"
                              }`}
                            >
                              <div className="flex items-center gap-4">
                                <div
                                  className={`h-5 w-5 rounded-full flex items-center justify-center border ${
                                    selectedOption === index
                                      ? "border-primary bg-primary text-white"
                                      : "border-gray-300"
                                  }`}
                                >
                                  {selectedOption === index && "\u2713"}
                                </div>
                                <MathJaxRenderer className="flex-1 whitespace-pre-line leading-relaxed">
                                  {option || ""}
                                </MathJaxRenderer>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {showExplanation &&
                    (currentQuestionIsDescriptive ? (
                      <div
                        className={`p-4 rounded-lg mb-6 ${
                          typeof isAnswerCorrect === "boolean"
                            ? isAnswerCorrect
                              ? "bg-green-50 border border-green-200"
                              : "bg-red-50 border border-red-200"
                            : "bg-gray-50 border border-gray-200"
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-700">
                              Evaluation Result
                            </p>
                            {/* {typeof isAnswerCorrect === "boolean" && (
                              <p
                                className={`text-base font-semibold ${
                                  isAnswerCorrect
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {isAnswerCorrect
                                  ? "Meets expectations"
                                  : "Needs improvement"}
                              </p>
                            )} */}
                          </div>
                          {(descriptiveScoreDisplay ||
                            descriptiveTotalDisplay) && (
                            <div className="text-right">
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Score
                              </p>
                              <p className="text-lg font-bold text-blue-600">
                                {descriptiveScoreDisplay || "0"}
                                {descriptiveTotalDisplay && (
                                  <>
                                    <span className="text-gray-500"> / </span>
                                    {descriptiveTotalDisplay}
                                  </>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                        {displayedCorrectOption && (
                          <div className="mb-3">
                            {/* <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                              Expected Answer
                            </p>
                            <MathJaxRenderer className="mt-1 whitespace-pre-line text-sm text-gray-700 leading-relaxed">
                              {displayedCorrectOption || ""}
                            </MathJaxRenderer> */}
                          </div>
                        )}
                        {descriptiveFeedbackText && (
                          <MathJaxRenderer className="mt-1 whitespace-pre-line leading-relaxed">
                            {descriptiveFeedbackText}
                          </MathJaxRenderer>
                        )}
                        {descriptiveStepBreakdown.length > 0 && (
                          <div className="mt-6">
                            <h4 className="text-sm font-semibold text-gray-800 mb-2">
                              Step-by-step Evaluation
                            </h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left py-2 px-3 font-medium text-gray-700 w-20 min-w-[80px]">
                                      Step
                                    </th>
                                    <th className="text-left py-2 px-3 font-medium text-gray-700">
                                      Solution
                                    </th>
                                    <th className="text-right py-2 px-3 font-medium text-gray-700">
                                      Marks
                                    </th>
                                    <th className="text-left py-2 px-3 font-medium text-gray-700">
                                      Justification
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {descriptiveStepBreakdown.map(
                                    (row, rowIndex) => (
                                      <tr
                                        key={rowIndex}
                                        className="border-b border-gray-100"
                                      >
                                        <td className="py-2 px-3 text-gray-600 font-medium w-20 min-w-[80px] whitespace-nowrap">
                                          {row?.step ?? rowIndex + 1}
                                        </td>
                                        <td className="py-2 px-3 text-gray-700 align-top">
                                          <FeedbackContent row={row} />
                                        </td>
                                        <td className="py-2 px-3 text-right text-gray-800 font-semibold align-top whitespace-nowrap">
                                          {formatMarks(row?.marks) || "0.0"}/
                                          {formatMarks(row?.maxMarks) || "0.0"}
                                        </td>
                                        <td className="py-2 px-3 text-gray-700 align-top">
                                          {row?.justification ? (
                                            <MathJaxRenderer className="leading-relaxed">
                                              {applyBasicMarkdown(
                                                row.justification
                                              )}
                                            </MathJaxRenderer>
                                          ) : (
                                            <span className="text-gray-400">
                                              -
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`p-4 rounded-lg mb-6 ${
                          isAnswerCorrect
                            ? "bg-green-50 border border-green-200"
                            : "bg-red-50 border border-red-200"
                        }`}
                      >
                        <div className="font-semibold mb-1">
                          {isAnswerCorrect
                            ? "\u2713 Correct!"
                            : "\u2717 Incorrect."}
                        </div>
                        {!isAnswerCorrect && displayedCorrectOption && (
                          <div className="text-sm text-gray-700 mb-2">
                            <span className="font-medium">Correct answer:</span>
                            <MathJaxRenderer className="mt-1 whitespace-pre-line leading-relaxed">
                              {displayedCorrectOption || ""}
                            </MathJaxRenderer>
                          </div>
                        )}
                        {/* {explanationText && (
                          <MathJaxRenderer className="mt-2 whitespace-pre-line leading-relaxed">
                            {explanationText || ""}
                          </MathJaxRenderer>
                        )} */}
                      </div>
                    ))}

                  <div className="flex justify-between items-center flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      {/* Show Get Hint before explanation, if a key hint exists */}
                      {!isMasteryModeActive &&
                        !showExplanation &&
                        currentKeyHint && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowHint(true);
                              setTakenHintByQuestion((prev) => {
                                const next = Array.isArray(prev)
                                  ? [...prev]
                                  : [];
                                while (next.length <= normalizedQuestionIndex) {
                                  next.push(false);
                                }
                                next[normalizedQuestionIndex] = true;
                                return next;
                              });
                            }}
                            disabled={showHint}
                            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                              showHint
                                ? "bg-yellow-100 border-yellow-300 text-yellow-800 cursor-default"
                                : "bg-white border-blue-300 text-primary hover:bg-primary-light hover:border-primary"
                            }`}
                            title="Less credit, if you see hint before trying yourself"
                          >
                            {showHint ? "Hint" : "Get Hint"}
                          </button>
                        )}

                      {/* Show Ask Aanya only after explanation is visible */}
                      {!isMasteryModeActive && showExplanation && (
                        <div className="relative group">
                          <button
                            onClick={() => openHelpPanel(null)}
                            className={`flex items-center gap-2 rounded-full p-2 transition-all duration-300 border ${
                              showSidePanel
                                ? "bg-blue-50 border-blue-400"
                                : "bg-transparent border-gray-300 hover:bg-gray-100 hover:border-blue-400"
                            }`}
                          >
                            <img
                              src="/images/tutor.webp"
                              alt="Need Help"
                              className="w-8 h-8 rounded-full object-cover object-top"
                            />
                            <span
                              className={`text-sm font-medium pr-1 ${
                                showSidePanel ? "text-primary" : "text-primary"
                              }`}
                            >
                              Ask Aanya?
                            </span>
                          </button>
                          <div className="absolute left-0 top-full mt-2 bg-gray-800 text-white text-xs rounded-lg py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
                            Open help side panel
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-800 transform rotate-45"></div>
                          </div>
                        </div>
                      )}

                      {!isMasteryModeActive && (
                        <button
                          onClick={handleCheckAnswer}
                          disabled={!canCheckAnswer}
                          className={`px-6 py-2 rounded-full font-medium ${
                            !canCheckAnswer
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-primary text-white hover:bg-primary-dark"
                          }`}
                        >
                          {checkAnswerButtonText}
                        </button>
                      )}
                      {/* Flag Issue button moved to header as icon-only */}
                    </div>
                    <div className="flex items-center gap-2">
                      {submittingAnswer ? (
                        <SimpleThreeDotsLoader message="Loading..." />
                      ) : (
                        <>
                          <button
                            onClick={handlePreviousQuestion}
                            disabled={currentQuestionIndex === 0}
                            className={`hidden px-4 py-2 rounded-full font-medium border border-gray-300 text-gray-700 transition-colors ${
                              currentQuestionIndex === 0
                                ? "bg-gray-100 cursor-not-allowed opacity-60"
                                : "bg-white hover:bg-gray-100"
                            }`}
                          >
                            Previous
                          </button>
                          <button
                            onClick={handleSkipQuestion}
                            disabled={!canSkipToNextQuestion}
                            className={`px-4 py-2 rounded-full font-medium border border-gray-300 transition-colors hidden ${
                              canSkipToNextQuestion
                                ? "text-gray-700 bg-white hover:bg-gray-100"
                                : "text-gray-400 bg-gray-100 cursor-not-allowed opacity-60"
                            }`}
                          >
                            Skip
                          </button>
                          <button
                            onClick={handleNextQuestion}
                            disabled={isNextDisabled}
                            className={`px-6 py-2 rounded-full font-medium ${
                              isNextDisabled
                                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                : "bg-primary text-white hover:bg-primary-dark"
                            }`}
                          >
                            {nextButtonLabel}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {showHint && currentKeyHint && (
                    <div className="mt-3 mb-4">
                      <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                        <p className="text-xs font-semibold text-yellow-800 uppercase tracking-wide mb-1">
                          Hint
                        </p>
                        <MathJaxRenderer className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">
                          {currentKeyHint}
                        </MathJaxRenderer>
                      </div>
                    </div>
                  )}
                  {/* Descriptive submission overlay loader */}
                  {submittingAnswer && currentQuestionIsDescriptive && (
                    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
                      <div className="bg-white rounded-2xl px-8 py-6 shadow-2xl max-w-sm w-full text-center flex flex-col items-center">
                        {/* Icon that switches based on status index */}
                        <div className="mb-4 flex items-center justify-center">
                          {(() => {
                            const iconIndex =
                              descriptiveStatusIndex %
                              DESCRIPTIVE_STATUS_MESSAGES.length;
                            const baseIconClass =
                              "w-10 h-10 text-blue-600 transition-transform duration-300 ease-out";
                            if (iconIndex === 0) {
                              // Thinking
                              return (
                                <svg
                                  className={baseIconClass}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12 3C8.686 3 6 5.686 6 9c0 2.136 1.07 3.667 2.5 4.7V16a1 1 0 001 1h1v2.5a1.5 1.5 0 103 0V17a3 3 0 00-3-3h-.5v-1.243C11.97 12.913 18 12.5 18 9c0-3.314-2.686-6-6-6z"
                                    fill="currentColor"
                                    opacity="0.9"
                                  />
                                </svg>
                              );
                            }
                            if (iconIndex === 1) {
                              // Reading
                              return (
                                <svg
                                  className={baseIconClass}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M4 5a2 2 0 012-2h5l1 1h6a2 2 0 012 2v11a1 1 0 01-1.447.894L17 17.118l-6 2.776-6-2.776-1.553.776A1 1 0 012 18V5z"
                                    fill="currentColor"
                                  />
                                </svg>
                              );
                            }
                            if (iconIndex === 2) {
                              // Analysing
                              return (
                                <svg
                                  className={baseIconClass}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M11 5a7 7 0 105.196 11.804l2.5 2.5a1 1 0 001.414-1.414l-2.5-2.5A7 7 0 0011 5zm-5 7a5 5 0 119.9 1h-2.152A3.001 3.001 0 0011 10a3 3 0 00-2.748 3H6z"
                                    fill="currentColor"
                                  />
                                </svg>
                              );
                            }
                            // Evaluating
                            return (
                              <svg
                                className={baseIconClass}
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 4a2 2 0 00-2 2v12a1 1 0 001.447.894L8 18.118l3.553 1.776a1 1 0 00.894 0L16 18.118l3.553 1.776A1 1 0 0021 18V6a2 2 0 00-2-2H5z"
                                  fill="currentColor"
                                  opacity="0.15"
                                />
                                <path
                                  d="M8 9h8M8 12h5M8 15h3"
                                  stroke="currentColor"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            );
                          })()}
                        </div>
                        <div className="mb-2">
                          <SimpleThreeDotsLoader
                            message={
                              isEvaluatingScore
                                ? ""
                                : "Designing your next questions"
                            }
                          />
                        </div>
                        {isEvaluatingScore && (
                          <p className="mt-2 text-sm text-gray-700 font-medium text-center">
                            {
                              DESCRIPTIVE_STATUS_MESSAGES[
                                descriptiveStatusIndex %
                                  DESCRIPTIVE_STATUS_MESSAGES.length
                              ]
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {previewAttachment && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
                      <div className="w-full max-w-4xl max-h-[90vh] bg-gray-900 rounded-3xl shadow-2xl flex flex-col overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-[#0D99FF] text-white">
                          <div className="text-sm font-semibold">
                            {previewAttachment.isPdf
                              ? "PDF Preview"
                              : "Image Preview"}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap justify-end text-xs sm:text-sm">
                            <div className="flex items-center gap-1 bg-white/15 rounded-full px-2 py-1">
                              <button
                                type="button"
                                onClick={decreasePreviewAttachmentZoom}
                                className="px-2 py-1 rounded-full hover:bg-white/20 transition-colors"
                              >
                                -
                              </button>
                              <span className="w-12 text-center font-semibold">
                                {Math.round(previewAttachmentZoom * 100)}%
                              </span>
                              <button
                                type="button"
                                onClick={increasePreviewAttachmentZoom}
                                className="px-2 py-1 rounded-full hover:bg-white/20 transition-colors"
                              >
                                +
                              </button>
                            </div>
                            {previewAttachmentCount > 1 && (
                              <div className="flex items-center gap-1 bg-white/15 rounded-full px-2 py-1">
                                <button
                                  type="button"
                                  onClick={goPrevPreviewAttachment}
                                  disabled={disablePreviewPrev}
                                  className="px-2 py-1 rounded-full hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  &lt;
                                </button>
                                <input
                                  type="number"
                                  min={1}
                                  max={previewAttachmentCount}
                                  value={previewAttachmentPageInput}
                                  onChange={handlePreviewAttachmentInputChange}
                                  onBlur={handlePreviewAttachmentInputBlur}
                                  onKeyDown={
                                    handlePreviewAttachmentInputKeyDown
                                  }
                                  className="w-14 h-8 rounded-full text-center text-gray-900 text-sm font-semibold"
                                />
                                <span className="font-semibold">
                                  / {previewAttachmentCount}
                                </span>
                                <button
                                  type="button"
                                  onClick={goNextPreviewAttachment}
                                  disabled={disablePreviewNext}
                                  className="px-2 py-1 rounded-full hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  &gt;
                                </button>
                              </div>
                            )}
                            {previewAttachment?.url && (
                              <button
                                type="button"
                                onClick={handlePreviewAttachmentOpenInNewTab}
                                className="px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors font-semibold"
                              >
                                Open
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={closeAttachmentPreview}
                              className="p-1 rounded-full hover:bg-white/20 transition-colors"
                              aria-label="Close file preview"
                            >
                              <FaTimes className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 bg-gray-900 flex items-center justify-center p-4 overflow-auto">
                          {previewAttachment.isPdf ? (
                            <div
                              className="bg-white rounded-2xl shadow-lg overflow-hidden"
                              style={{
                                transform: `scale(${previewAttachmentZoom})`,
                                transformOrigin: "top center",
                              }}
                            >
                              <object
                                data={previewAttachment.url}
                                type="application/pdf"
                                className="w-[min(900px,70vw)] h-[70vh] bg-white"
                              >
                                <p className="p-4 text-sm text-gray-700">
                                  Preview not supported.{" "}
                                  <a
                                    href={previewAttachment.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 underline"
                                  >
                                    Open the PDF in a new tab
                                  </a>
                                  .
                                </p>
                              </object>
                            </div>
                          ) : (
                            <img
                              src={previewAttachment.url}
                              alt={previewAttachment.name || "Uploaded file"}
                              className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-lg bg-black"
                              style={{
                                transform: `scale(${previewAttachmentZoom})`,
                                transformOrigin: "top center",
                              }}
                            />
                          )}
                        </div>
                        {previewAttachment?.name && (
                          <div className="px-4 py-2 bg-gray-900 border-t border-gray-800 text-center text-xs sm:text-sm text-gray-200 truncate">
                            {previewAttachment.name}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {showImageDeleteConfirm && imageToDelete && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Remove file?
                          </h3>
                        </div>
                        <div className="px-6 py-4">
                          <p className="text-gray-700 mb-1">
                            Are you sure you want to delete this file?
                          </p>
                          <p className="text-xs text-gray-500">
                            This cannot be undone.
                          </p>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
                          <button
                            onClick={handleCancelDeleteImage}
                            className="px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleConfirmDeleteImage}
                            className="px-6 py-2 rounded-full text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {showCameraModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            Interactive Learning
                          </h3>
                          <button
                            onClick={handleCloseCameraModal}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="Close camera"
                          >
                            <FaTimes size={20} />
                          </button>
                        </div>
                        <div className="p-6">
                          <div className="bg-gray-100 rounded-lg overflow-hidden mb-4">
                            <video
                              ref={videoRef}
                              autoPlay
                              playsInline
                              muted
                              className="w-full h-auto"
                              style={{ maxHeight: "60vh" }}
                            />
                          </div>
                          <div className="text-center mb-4">
                            <p className="text-sm text-gray-600">
                              Point at your Solution and click capture.
                            </p>
                          </div>
                          <div className="flex justify-center gap-3">
                            <button
                              onClick={handleCloseCameraModal}
                              className="px-6 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleCapturePhoto}
                              className="px-8 py-2 rounded-full text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                              Capture
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {capturePreviewData && (
                    <div className="fixed max-h-auto inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
                      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col">
                        <button
                          onClick={handleCancelCapturePreview}
                          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label="Close crop modal"
                        >
                          <FaTimes size={20} />
                        </button>
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Preview & edit captured image
                          </h3>
                        </div>
                        <div className="p-6 space-y-4 flex-1 flex flex-col">
                          <div
                            ref={cropperContainerRef}
                            className="relative w-full flex-1 min-h-[100px] bg-black rounded-2xl overflow-hidden"
                          >
                            <ReactCrop
                              crop={captureCrop}
                              onChange={(nextCrop) => setCaptureCrop(nextCrop)}
                              onComplete={(finalCrop) =>
                                setCaptureCompletedCrop(finalCrop)
                              }
                              className="h-full w-full"
                            >
                              <img
                                src={capturePreviewData.url}
                                alt="Captured preview"
                                className="h-full w-full object-contain"
                                onLoad={(e) =>
                                  setCaptureImageDimensions({
                                    naturalWidth:
                                      e?.currentTarget?.naturalWidth ?? null,
                                    naturalHeight:
                                      e?.currentTarget?.naturalHeight ?? null,
                                    renderedWidth:
                                      e?.currentTarget?.getBoundingClientRect()
                                        ?.width ??
                                      e?.currentTarget?.width ??
                                      e?.currentTarget?.naturalWidth ??
                                      null,
                                    renderedHeight:
                                      e?.currentTarget?.getBoundingClientRect()
                                        ?.height ??
                                      e?.currentTarget?.height ??
                                      e?.currentTarget?.naturalHeight ??
                                      null,
                                  })
                                }
                              />
                            </ReactCrop>
                          </div>
                          <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                              Drag or resize the crop box to focus on your
                              preferred region. Use rotation if the capture is
                              tilted.
                            </p>
                            <div className="flex flex-col gap-2">
                              {/* <div className="flex items-center justify-between text-sm text-gray-600">
                                <span>Rotate</span>
                                <span>{Math.round(captureRotation)} deg</span>
                              </div>
                              <input
                                type="range"
                                min={0}
                                max={360}
                                step={1}
                                value={captureRotation}
                                onChange={(event) =>
                                  setCaptureRotation(Number(event.target.value))
                                }
                                className="w-full"
                              /> */}
                              {/* <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCaptureRotation(
                                      (prev) => (prev + 90) % 360
                                    )
                                  }
                                  className="px-3 py-1.5 rounded-full border border-gray-300 text-xs font-medium text-gray-700 hover:border-blue-500 hover:text-blue-600 transition-colors"
                                >
                                  Rotate +90 deg
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCaptureRotation(
                                      (prev) => (prev - 90 + 360) % 360
                                    )
                                  }
                                  className="px-3 py-1.5 rounded-full border border-gray-300 text-xs font-medium text-gray-700 hover:border-blue-500 hover:text-blue-600 transition-colors"
                                >
                                  Rotate -90 deg
                                </button>
                              </div> */}
                              <button
                                type="button"
                                onClick={handleConfirmCapturePreview}
                                className={`mt-2 inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-colors ${
                                  isProcessingCapturePreview
                                    ? "bg-blue-300 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700"
                                }`}
                                disabled={isProcessingCapturePreview}
                              >
                                {isProcessingCapturePreview
                                  ? "Addding..."
                                  : "Add image"}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
                          <button
                            onClick={handleCancelCapturePreview}
                            className="px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                            disabled={isProcessingCapturePreview}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {showCloseConfirmation && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Leave Quiz?
                          </h3>
                        </div>
                        <div className="px-6 py-4">
                          <p className="text-gray-700 mb-4">
                            Are you sure you want to leave?
                          </p>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
                          <button
                            onClick={() => setShowCloseConfirmation(false)}
                            className="px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            Cancel
                          </button>
                          {showViewResult && (
                            <button
                              onClick={() => {
                                setShowCloseConfirmation(false);
                                void completeQuiz();
                              }}
                              className="px-6 py-2 rounded-full text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                            >
                              View Results
                            </button>
                          )}
                          {!showViewResult && (
                            <button
                              onClick={() => {
                                setShowCloseConfirmation(false);
                                if (embedded && onClose) {
                                  onClose();
                                } else {
                                  navigate("/");
                                }
                              }}
                              className="px-6 py-2 rounded-full text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
                            >
                              Leave Quiz
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {showFlagModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Why are you flagging this question?
                          </h3>
                          <button
                            onClick={handleCloseFlagModal}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="Close flag question dialog"
                          >
                            <FaTimes />
                          </button>
                        </div>
                        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                          <div className="space-y-2">
                            {FLAG_REASON_OPTIONS.map((reason) => (
                              <label
                                key={reason}
                                className={`flex items-start gap-3 rounded-2xl border px-4 py-3 cursor-pointer transition-colors ${
                                  selectedFlagReason === reason
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-300"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="flagReason"
                                  value={reason}
                                  checked={selectedFlagReason === reason}
                                  onChange={() => setSelectedFlagReason(reason)}
                                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">
                                  {reason}
                                </span>
                              </label>
                            ))}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Please explain
                            </label>
                            <textarea
                              value={flagExplanation}
                              onChange={(e) =>
                                setFlagExplanation(e.target.value)
                              }
                              placeholder="Share any details that will help us fix the issue."
                              rows={4}
                              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            />
                            {selectedFlagReason === "Other, please specify" &&
                              !flagExplanation.trim() && (
                                <p className="mt-1 text-xs text-red-600">
                                  Please provide some details for this
                                  selection.
                                </p>
                              )}
                          </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
                          <button
                            onClick={handleCloseFlagModal}
                            className="px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                            disabled={isSubmittingFlag}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSubmitFlag}
                            disabled={
                              isSubmittingFlag ||
                              !selectedFlagReason ||
                              (selectedFlagReason === "Other, please specify" &&
                                !flagExplanation.trim())
                            }
                            className={`px-6 py-2 rounded-full text-sm font-semibold text-white transition-colors ${
                              isSubmittingFlag ||
                              !selectedFlagReason ||
                              (selectedFlagReason === "Other, please specify" &&
                                !flagExplanation.trim())
                                ? "bg-blue-300 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                            }`}
                          >
                            {isSubmittingFlag ? "Submitting..." : "Submit"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </MathJaxContext>
        )}
      </div>
    </div>
  );

  // ======= FINAL RETURN: Split screen with draggable divider =======
  return (
    <>
      <div className="flex h-screen w-full">
        <div className="flex flex-1 " ref={resizeRef}>
          {/* Left Panel - GoalQuestions main content */}
          <div
            className={`flex flex-col bg-gray-50 ${
              isMobile ? "" : showSidePanel ? "border-r border-gray-200" : ""
            }`}
            style={{
              width: isMobile || !showSidePanel ? "100%" : `${leftPanelWidth}%`,
            }}
          >
            {LeftPanelContent}
          </div>

          {/* Resizer - desktop only, visible only after Need Help */}
          {!isMobile && showSidePanel && (
            <div
              className="w-4 h-full flex flex-col justify-center items-center cursor-col-resize"
              onMouseDown={handleMouseDown}
              title="Drag to resize"
            >
              <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
              <div className="p-1 rounded-md bg-white border border-gray-400 shadow-md transition-all duration-200 hover:bg-gray-100">
                <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                  <div className="w-1 h-1 bg-gray-700 rounded-full" />
                  <div className="w-1 h-1 bg-gray-700 rounded-full" />
                  <div className="w-1 h-1 bg-gray-700 rounded-full" />
                  <div className="w-1 h-1 bg-gray-700 rounded-full" />
                  <div className="w-1 h-1 bg-gray-700 rounded-full" />
                  <div className="w-1 h-1 bg-gray-700 rounded-full" />
                </div>
              </div>
              <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
            </div>
          )}

          {/* Right Panel - Placeholder red p area (desktop only w, after Need Help) */}
          {!isMobile && showSidePanel && (
            <div
              className="flex flex-col overflow-y-auto"
              style={{
                width: `${100 - leftPanelWidth}%`,
              }}
            >
              <DoubtSolver
                disableWorkbookMode={true}
                prefilledTextProp={questionWithOptions}
                autoSendPrefilledText={false}
              />
            </div>
          )}
        </div>
      </div>
      {showLevelUpCelebration && (
        <div className="pointer-events-none fixed inset-0 z-[9999] flex flex-col items-center justify-center">
          <div className="relative flex items-center justify-center">
            <dotlottie-wc
              src="/fireworks.lottie"
              autoplay
              loop
              style={{
                width: "460px",
                height: "460px",
                position: "absolute",
                zIndex: 1,
              }}
            />
            <dotlottie-wc
              autoplay
              loop
              ref={levelUpPlayerRef}
              src="/Clapping_primary.lottie"
              style={{
                width: "320px",
                height: "320px",
                position: "relative",
                zIndex: 2,
              }}
            />
          </div>

          <p className="mt-4 text-3xl font-semibold uppercase tracking-wide text-black drop-shadow-lg">
            Level increased
          </p>
        </div>
      )}
    </>
  );
};

export default GoalQuestions;
